import { PDFDocument } from 'pdf-lib';
import { PDFName,PDFCheckBox } from 'pdf-lib';
import { rgb, StandardFonts } from 'pdf-lib';
import { filingDebtors } from '../../utils/debtors';
import {
  amendmentServiceDescription,
  amendmentServiceRecipients,
  appendAmendmentServiceList,
} from '../../utils/creditorMailingMatrix';

// Helper to log form field names
const API_URL2 = process.env.REACT_APP_BACKEND_URL;
const isoToday = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
async function generateCreditorsListPdf(creditors) {
    // Create the document
    const listDoc = await PDFDocument.create();
    creditors = Array.isArray(creditors) ? creditors : [];
    
    // Deduplicate by name & address
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  
    const helvetica = await listDoc.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792]; // Letter size
    let page = listDoc.addPage(pageSize);
    const { width, height } = page.getSize();
    const margin = 50;
    let y = height - margin;
    const lineHeight = 12;
    const indent = margin + 10;

    if (creditors.length === 0) {
      const message = 'No additional creditors were served.';
      const fontSize = 11;
      const textWidth = helvetica.widthOfTextAtSize(message, fontSize);
      page.drawText(message, {
        x: (width - textWidth) / 2,
        y: height - 100,
        size: fontSize,
        font: helvetica,
      });
      return listDoc;
    }
  
    for (const c of creditors) {
      // Name
      page.drawText(c.name, { x: margin, y, size: 10, font: helvetica });
      y -= lineHeight;
      // Address line 1
      page.drawText(
        `${c.street1}${c.street2?.trim() ? ', ' + c.street2 : ''}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // City, State Zip
      page.drawText(
        `${c.city}, ${c.state} ${c.zip_code}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // Method
      page.drawText(`Served via: ${c.method}`, { x: indent, y, size: 10, font: helvetica });
      y -= lineHeight * 1.5;
  
      if (y < margin) {
        page = listDoc.addPage(pageSize);
        y = height - margin;
      }
    }
  
    return listDoc;
  }
function moveUncheckedCheckboxesToCorner(form, pageSize, context) {
  const { width, height } = pageSize;
  form.getFields().forEach(field => {
    // Only target PDFCheckBox fields
    if (field instanceof PDFCheckBox) {
      try {
        if (!field.isChecked()) {
          const widgets = field.acroField.getWidgets();
          widgets.forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            dict.set(
              PDFName.of('Rect'),
              context.obj([
                width + 5,
                height + 5,
                width + 15,
                height + 15
              ])
            );
          });
        }
      } catch {
        // ignore any errors moving individual checkboxes
      }
    }
  });
}
  async function generateEnvelopesPdf(creditors) {
    if (!creditors || creditors.length === 0) return;
    const envDoc = await PDFDocument.create();
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // 1. Load & embed your logo (adjust the path as needed)
    const logoUrl = '/images/logo.png';
    const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
    const logoImage = await envDoc.embedPng(logoBytes);
    const logoDims = logoImage.scale(0.1);
  
    // 2. Prepare font and layout constants
    const font = await envDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 10;
    const lineHeight = fontSize + 4;
    const envelopeSize = [9.5 * 72, 4.125 * 72]; // width × height in points
    const margin = 25;
  
    // Your return address lines
    const returnAddress = [
      'Cisowski Law',
      '3119 Bristol Hwy Ste 217',
      'Johnson City, TN 37601',
    ];
  
    // 3. One page per creditor
    for (const c of creditors) {
      const page = envDoc.addPage(envelopeSize);
      const { width, height } = page.getSize();
  
      // 3a. Draw logo in top-left
      page.drawImage(logoImage, {
        x: margin,
        y: height - margin - logoDims.height,
        width: logoDims.width,
        height: logoDims.height,
      });
  
      // 3b. Draw return address to the right of the logo
      let yPos = height - margin - 9;
      for (const line of returnAddress) {
        page.drawText(line, {
          x: margin + logoDims.width + 10,
          y: yPos,
          size: fontSize,
          font,
        });
        yPos -= fontSize + 2;
      }
  
      // draw recipient address centered vertically and horizontally
      const addrLines = [
        c.name,
        `${c.street1}${c.street2?.trim() ? `, ${c.street2}` : ''}`,
        `${c.city}, ${c.state} ${c.zip_code}`,
      ];
      // measure max line width
      const lineWidths = addrLines.map(line => font.widthOfTextAtSize(line, fontSize));
      const maxWidth = Math.max(...lineWidths);
      const startX = (width - maxWidth) / 2;
      let startY = height / 2 + (addrLines.length / 2) * lineHeight;
      for (const line of addrLines) {
        page.drawText(line, {
          x: startX,
          y: startY,
          size: fontSize,
          font,
        });
        startY -= lineHeight;
      }
    }
  
    // 4. Save & trigger download
    const pdfBytes = await envDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'envelopes.pdf';
    link.click();
  }
function logFieldNames(form, label) {
  console.log(`--- ${label} Fields ---`);
  form.getFields().forEach(field => {
    console.log(field.getName());
  });
}

const fmtDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-');
      return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string, e.g. "Thursday, May 29, 2025"
    const fmtLongDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-').map(Number);
      // Construct date in local timezone to avoid UTC offset issues
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

// Format YYYY-MM or YYYY-MM-DD as MM/YYYY
const fmtMMYYYY = (iso) => {
  if (!iso) return '';
  const [y, m] = iso.split('-');
  return `${m}/${y}`;
};

// Read a boolean-like querystring flag (?debug_all_insiders=1)
const queryFlag = (key) => {
  try {
    const v = new URLSearchParams(window.location.search).get(key);
    return /^(1|true|yes)$/i.test(String(v || ''));
  } catch { return false; }
};

function drawTableRows(page, font, rows, startY, colDefs, rowHeight) {
  // colDefs: [{key, x, width, wrap?: boolean}]
  // wraps simple, without hyphenation (sufficient for our continuation pages)
  let y = startY;
  const size = 10;

  const wrapText = (text, maxWidth) => {
    if (!text) return [''];
    const words = String(text).split(/\s+/);
    const lines = [];
    let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (font.widthOfTextAtSize(test, size) <= maxWidth) {
        line = test;
      } else {
        if (line) lines.push(line);
        line = w;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  for (const r of rows) {
    // determine tallest wrapped cell
    let maxLines = 1;
    const wrapped = {};
    for (const c of colDefs) {
      const val = r[c.key] ?? '';
      if (c.wrap) {
        wrapped[c.key] = wrapText(val, c.width);
        maxLines = Math.max(maxLines, wrapped[c.key].length);
      }
    }
    // draw each line of this row
    for (let i = 0; i < maxLines; i++) {
      for (const c of colDefs) {
        const val = r[c.key] ?? '';
        const text = c.wrap ? (wrapped[c.key][i] || '') : String(val);
        page.drawText(String(text), { x: c.x, y, size, font });
      }
      y -= rowHeight;
      if (y < 50) {
        return { y, overflow: true };
      }
    }
  }
  return { y, overflow: false };
}

async function fetchPdf(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch PDF: ' + url);
  return new Uint8Array(await res.arrayBuffer());
}

export async function generateSofa(caseData, sofaData, amendedData) {
  let blob;
  try {
    // IMPORTANT: This generator no longer auto-fetches any SOFA payload.
    // It must be called with a prebuilt object that already contains `.sofa`.
    // Prefer `sofaData` if provided; otherwise allow legacy `caseData` if it has `.sofa`.
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

    const rawData =
      (sofaData && sofaData.sofa) ? sofaData :
      (caseData && caseData.sofa) ? caseData :
      null;

    if (!rawData || !rawData.sofa) {
      throw new Error(
        'generateSofa: Missing SOFA payload. Pass an object that includes a `.sofa` property.'
      );
    }

    const orderedFilingDebtors = filingDebtors(
      rawData.debtors || rawData.caption?.debtors,
    );
    const data = {
      ...rawData,
      debtors: orderedFilingDebtors,
      caption: { ...(rawData.caption || {}), debtors: orderedFilingDebtors },
    };

    // 1) Load official SOFA template and create an output doc
    const sofaUrl = '/forms/sofa.pdf';
    const sofaBytes = await fetchPdf(sofaUrl);
    const sofaPdf = await PDFDocument.load(sofaBytes);
    const out = await PDFDocument.create();

    // 2) Fill Page 1 on a clone, then copy the filled page into output.
    try {
      const sofaPdfClone = await PDFDocument.load(sofaBytes);
      const form = sofaPdfClone.getForm();
      const debtors = data.debtors || data.caption?.debtors || [];
      const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
      const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};

      const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
      const d1Name = fullName(d1);
      const d2Name = fullName(d2);
      const caseNo = data.caption?.case_number || data.case_number || '';
      const chapter = data.caption?.chapter || data.chapter || '';
      const married = data.sofa?.q1?.married;

      const trySet = (name, val) => { try { form.getTextField(name).setText(val ?? ''); } catch {} };
      const tryCheck = (name) => { try { form.getCheckBox(name).check(); } catch {} };

      // Normalize field names for fuzzy comparisons
      const normalizeName = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '_');

      // Robust setter: scan every form field and set any text field whose name matches
      // any candidate (exact, normalized, or substring match of normalized names)
      const setByCandidates = (candidates, val) => {
        if (val == null) val = '';
        const fields = form.getFields();
        const norms = candidates.map(c => normalizeName(c));
        fields.forEach(f => {
          const nm = (f.getName ? f.getName() : '').toString();
          const norm = normalizeName(nm);
          const hit = norms.some(cn => norm === cn || norm.includes(cn));
          if (hit) {
            try { form.getTextField(nm).setText(val); console.debug('Set field', nm, '=>', val); } catch {}
          }
        });
      };

      // Debtor 1 and Debtor 2 full names — try many variants and fuzzy matches
      form.getTextField('Debtor 1').setText(d1Name)
      form.getTextField('Debtor 2').setText(d2Name)
      console.log(amendedData)
      if (amendedData.amended) form.getCheckBox('amended').check()
      // Case number & chapter — broad matching of common label forms
      setByCandidates(['case_number', 'Case Number', 'CaseNumber', 'case no', 'Case No', 'CaseNo', 'Bk Case No', 'Bankruptcy Case Number'], caseNo);
      setByCandidates(['chapter', 'Chapter', 'Bk Chapter'], chapter);
      if (married) ['married','married_yes','Married'].forEach(tryCheck); else ['not_married','married_no','Unmarried'].forEach(tryCheck);

      // ===== Q3: Community property state/territory in last 8 years? =====
      const community = data.sofa?.q3?.community_property;
      if (community === true) {
        ['community_state_yes','q3_yes','CommunityYes','community_yes','Community Property Yes'].forEach(tryCheck);
      } else if (community === false) {
        ['community_state_no','q3_no','CommunityNo','community_no','Community Property No'].forEach(tryCheck);
      }

      // ===== Q2: Prior addresses (fill on the official Page 1) =====
      const q2 = (data.sofa && data.sofa.q2) || {};
      const d1Addrs = (q2.primary && q2.primary.prior_addresses) || [];
      const d2Addrs = (q2.secondary && q2.secondary.prior_addresses) || [];

      const sortAddrs = (arr) => (arr || []).slice().sort((a, b) => {
        const aKey = (a.move_out_date || a.move_in_date || '0000-00').slice(0, 10);
        const bKey = (b.move_out_date || b.move_in_date || '0000-00').slice(0, 10);
        return aKey === bKey ? 0 : (aKey < bKey ? 1 : -1);
      });

      const d1Top = sortAddrs(d1Addrs).slice(0, 2);
      const d2Top = sortAddrs(d2Addrs).slice(0, 2);

      const addrEquals = (a, b) => {
        if (!a || !b) return false;
        const norm = (s) => (s || '').toString().trim().toUpperCase();
        return (
          norm(a.street1) === norm(b.street1) &&
          norm(a.street2) === norm(b.street2) &&
          norm(a.city) === norm(b.city) &&
          norm(a.state) === norm(b.state) &&
          norm(a.zip_code) === norm(b.zip_code)
        );
      };
      const matchesAnyD1Top = (a) => addrEquals(a, d1Top[0]) || addrEquals(a, d1Top[1]);

      const fillRow = (which, idx, a) => {
        const prefix = which === 'd1' ? 'Debtor 1' : 'Debtor 2';
        const map = {
          street1: `${prefix}_prior_street1_${idx}`,
          street2: `${prefix}_prior_street2_${idx}`,
          city:    `${prefix}_prior_city_${idx}`,
          state:   `${prefix}_prior_state_${idx}`,
          zip:     `${prefix}_prior_zip_${idx}`,
          from:    `${prefix}_prior_date_start_${idx}`,
          to:      `${prefix}_prior_date_end_${idx}`,
        };
        const setText = (fieldName, val) => { try { form.getTextField(fieldName).setText(val ?? ''); } catch {} };
        setText(map.street1, a?.street1 || '');
        setText(map.street2, a?.street2 || '');
        setText(map.city,    a?.city    || '');
        setText(map.state,   a?.state   || '');
        setText(map.zip,     a?.zip_code|| '');
        setText(map.from,    a?.move_in_date ? fmtMMYYYY(a.move_in_date) : '');
        setText(map.to,      a?.move_out_date ? fmtMMYYYY(a.move_out_date) : 'Present');
      };

      try {
        const hasAnyPrior = (d1Addrs && d1Addrs.length > 0) || (d2Addrs && d2Addrs.length > 0);
        if (hasAnyPrior) { form.getCheckBox('prior_address_yes').check(); } else { form.getCheckBox('prior_address_no').check(); }
      } catch {}

      const checkBoxSafe = (name) => { try { form.getCheckBox(name).check(); } catch {} };

      if (d1Top[0]) fillRow('d1', 1, d1Top[0]);
      if (d1Top[1]) fillRow('d1', 2, d1Top[1]);

      // Map Debtor 2 rows to Debtor 1 rows.
      // Rules:
      //  - If a D2 top-row address matches the corresponding D1 *top* row, check "Same as Debtor 1" for that row on Page 1 and DO NOT print D2 text.
      //  - If a D2 top-row address matches ANY D1 address that is NOT on Page 1 (i.e., in D1 overflow), suppress printing on Page 1 entirely; the continuation sheet will show the checkbox there.
      //  - Only D2 addresses that do not match ANY D1 address get printed as their own text on Page 1.
      const d1AllSorted = sortAddrs(d1Addrs); // includes overflow
      const d2UsedTop = [false, false];
      const sameAs = { 1: false, 2: false };

      const matchesAny = (a, arr) => arr.some(b => addrEquals(a, b));

      // First pass: set same-as for rows where D2Top matches the corresponding D1Top row
      [1, 2].forEach(i => {
        const d1Row = d1Top[i - 1];
        if (!d1Row) return;
        for (let j = 0; j < 2; j++) {
          if (!d2Top[j] || d2UsedTop[j]) continue;
          if (addrEquals(d2Top[j], d1Row)) {
            sameAs[i] = true;
            d2UsedTop[j] = true; // consumed — do not render text on Page 1
            break;
          }
        }
      });

      // Apply checkboxes on Page 1 for the aligned rows
      if (sameAs[1]) { checkBoxSafe('same_as_debtor_1'); checkBoxSafe('same_as_debtor_date_1'); }
      if (sameAs[2]) { checkBoxSafe('same_as_debtor_2'); checkBoxSafe('same_as_debtor_date_2'); }

      // Second pass: for each remaining D2Top item:
      //   - If it matches ANY D1 address (even if not on Page 1), suppress (the continuation page will handle it).
      //   - Else, fill it into the next available D2 row that is not marked same-as.
      const fillOrder = [1, 2].filter(i => !sameAs[i]);
      let nextSlot = 0;
      for (let j = 0; j < 2; j++) {
        if (!d2Top[j] || d2UsedTop[j]) continue; // already handled as same-as
        const d2Addr = d2Top[j];
        if (matchesAny(d2Addr, d1AllSorted)) {
          // It matches a D1 address that is *not* printed on Page 1 (overflow) — suppress here.
          continue;
        }
        const rowNum = fillOrder[nextSlot++];
        if (!rowNum) break; // no slots left
        fillRow('d2', rowNum, d2Addr);
      }

      moveUncheckedCheckboxesToCorner(form, sofaPdfClone.getPages()[0].getSize(), sofaPdfClone.context);
      form.flatten();

      const tmpBytes = await sofaPdfClone.save();
      const tmpDoc = await PDFDocument.load(tmpBytes);
      const [filledP1] = await out.copyPages(tmpDoc, [0]);
      out.addPage(filledP1);
    } catch (e) {
      console.warn('Falling back to blank Page 1 due to fill error:', e);
      const [blankP1] = await out.copyPages(sofaPdf, [0]);
      out.addPage(blankP1);
    }
// 3) Build Q2 overflow continuation pages (inserted immediately after Page 1)
{
  const q2 = data.sofa?.q2 || {};
  const d1Addrs = q2.primary?.prior_addresses || [];
  const d2Addrs = q2.secondary?.prior_addresses || [];

  // Respect two on-page rows per debtor; overflow is what's left
  const CAP = 2;
  const sortAddrs = (arr) => (arr || []).slice().sort((a, b) => {
    const ak = (a.move_out_date || a.move_in_date || '0000-00').slice(0, 10);
    const bk = (b.move_out_date || b.move_in_date || '0000-00').slice(0, 10);
    return ak === bk ? 0 : (ak < bk ? 1 : -1); // newest first
  });

  const d1Overflow = sortAddrs(d1Addrs).slice(CAP);
  const d2OverflowAll = sortAddrs(d2Addrs).slice(CAP);
  const d2AllSorted = sortAddrs(d2Addrs);

  if (d1Overflow.length || d2OverflowAll.length) {
    const helv = await out.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792];
    const [W, H] = pageSize;

    const debtors = data.debtors || [];
    const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
    const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
    const d1Name = [d1.first_name, d1.second_name, d1.last_name].filter(Boolean).join(' ');
    const d2Name = [d2.first_name, d2.second_name, d2.last_name].filter(Boolean).join(' ');
    const caseNo = data.caption?.case_number || data.case_number || '';

    // Address equality ignoring dates (uppercased, trimmed)
    const addrEquals = (a, b) => {
      if (!a || !b) return false;
      const N = (s) => (s || '').toString().trim().toUpperCase();
      return N(a.street1)===N(b.street1) && N(a.street2)===N(b.street2) &&
             N(a.city)===N(b.city) && N(a.state)===N(b.state) && N(a.zip_code)===N(b.zip_code);
    };

    // Match D2 rows to D1 rows where the address is identical, to show the “Same as Debtor 1” box
    const d2Used = new Array(d2OverflowAll.length).fill(false);
    const rowPairs = []; // [{d1, d2, sameAs}]
    for (const d1a of d1Overflow) {
      let matchedIdx = -1;
      let matchedInAnyD2 = false;
      // First, try to match against overflow entries so we can consume them
      for (let j = 0; j < d2OverflowAll.length; j++) {
        if (d2Used[j]) continue;
        if (addrEquals(d1a, d2OverflowAll[j])) { matchedIdx = j; matchedInAnyD2 = true; break; }
      }
      // If not found in overflow, check the full D2 list (including Page 1 top rows)
      if (!matchedInAnyD2) {
        for (let k = 0; k < d2AllSorted.length; k++) {
          if (addrEquals(d1a, d2AllSorted[k])) { matchedInAnyD2 = true; break; }
        }
      }
      if (matchedIdx >= 0) {
        // Found match in overflow -> mark same-as and consume that overflow D2 row
        rowPairs.push({ d1: d1a, d2: null, sameAs: true });
        d2Used[matchedIdx] = true;
      } else if (matchedInAnyD2) {
        // Match exists only in D2 top rows (already shown on Page 1) -> mark same-as, do not consume
        rowPairs.push({ d1: d1a, d2: null, sameAs: true });
      } else {
        // No D2 match at all -> plain D1-only row
        rowPairs.push({ d1: d1a, d2: null, sameAs: false });
      }
    }
    // Any remaining D2 rows that didn’t match get their own lines on the right
    for (let j = 0; j < d2OverflowAll.length; j++) {
      if (!d2Used[j]) rowPairs.push({ d1: null, d2: d2OverflowAll[j], sameAs: false });
    }

    // Drawer helpers
    const drawAddrBlock = (page, x, y, a) => {
      const size = 10;
      const yy = y - 2;
      if (!a) {
        page.drawText('—', { x, y: yy - 12, size, font: helv });
        return yy - 46;
      }
      const line1 = `${a.street1}${a.street2?.trim() ? `, ${a.street2}` : ''}`;
      page.drawText(line1, { x, y: yy, size, font: helv });
      page.drawText(`${(a.city||'').toUpperCase()}  ${(a.state||'').toUpperCase()}  ${(a.zip_code||'')}`,
        { x, y: yy - 14, size, font: helv });
      const from = a.move_in_date ? fmtMMYYYY(a.move_in_date) : '';
      const to   = a.move_out_date ? fmtMMYYYY(a.move_out_date) : 'Present';
      page.drawText(`From ${from}   To ${to}`, { x, y: yy - 28, size, font: helv });
      return yy - 46; // next y (row spacing)
    };

    const drawCheckbox = (page, x, y, checked, label='Same as Debtor 1') => {
      const box = 10;
      page.drawRectangle({ x, y: y - box, width: box, height: box, borderWidth: 1, color: rgb(1,1,1) });
      if (checked) page.drawText('X', { x: x + 1.5, y: y - box + 1, size: 12, font: helv });
      page.drawText(label, { x: x + box + 6, y: y - 9, size: 9, font: helv });
    };

    // page layout (mirrors Page 1 style)
    const leftX  = 50;
    const rightX = 330;
    const rowGap = 64; // vertical space per pair

    let idx = 0;
    while (idx < rowPairs.length) {
      const page = out.addPage(pageSize);

      // Header
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs\nPart 1, Question 2: Prior addresses (last 3 years)',
        { x: leftX, y: H - 50, size: 14, font: helv });
      // Removed debtor/case header line for cleaner look
      // const header2 = `Debtor 1: ${d1Name}${d2Name ? '    Debtor 2: ' + d2Name : ''}${caseNo ? '    Case No.: ' + caseNo : ''}`;
      // page.drawText(header2, { x: leftX, y: H - 120, size: 10, font: helv });

      // Column titles
      page.drawText('Debtor 1', { x: leftX,  y: H - 95, size: 10, font: helv });
      page.drawText('Debtor 2', { x: rightX, y: H - 95, size: 10, font: helv });

      // Rows (about 8 pairs per page at this spacing)
      let y = H - 110;
      while (idx < rowPairs.length && y > 80) {
        const row = rowPairs[idx];

        // Draw light background shading behind the row (span both columns)
        page.drawRectangle({
          x: leftX - 5,
          y: y - rowGap + 20,
          width: (W - leftX * 2) + 10,
          height: rowGap - 10,
          color: rgb(0.95, 0.95, 0.95)
        });

        // Left block (D1)
        const nextYLeft = drawAddrBlock(page, leftX, y, row.d1);

        // Right block (D2 or “same as” checkbox + empty address)
        if (row.sameAs) {
          drawCheckbox(page, rightX, y - 10, true);
          drawAddrBlock(page, rightX, y, null);
        } else {
          drawAddrBlock(page, rightX, y, row.d2 || null);
        }

        // Draw horizontal divider line under the row (across both columns)
        page.drawLine({
          start: { x: leftX, y: y - 4 },
          end: { x: W - leftX, y: y - 4 },
          thickness: 0.5,
          color: rgb(0.8, 0.8, 0.8)
        });

        // advance to next pair; keep rows aligned by using the left block height
        y = Math.min(nextYLeft, y - rowGap);
        idx++;
      }
    }
  }
}

// 3.5) Fill Page 2 (Q4 & Q5) on a clone, then copy into output
try {
  const sofaPdfClone2 = await PDFDocument.load(sofaBytes);
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors = data.debtors || data.caption?.debtors || [];
  const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
  const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
  const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  const d1Name = fullName(d1);
  const d2Name = fullName(d2);
  const form2 = sofaPdfClone2.getForm();
  form2.getTextField('case_number').setText(caseNo)
  form2.getTextField('Debtor 1').setText(d1Name)
  form2.getTextField('Debtor 2').setText(d2Name)
  const trySet2 = (name, val) => { try { form2.getTextField(name).setText(val ?? ''); } catch {} };
  const tryCheck2 = (name) => { try { form2.getCheckBox(name).check(); } catch {} };

  // Work on page index 1 (second page)
  const pageSize2 = sofaPdfClone2.getPages()[1]?.getSize?.() || sofaPdfClone2.getPages()[0].getSize();

  // ===== Q4: Employment/Business income (current, previous, two years ago) =====
  const q4 = data.sofa?.q4 || {};
  const d1Q4 = q4.primary?.employment_income_items || [];
  const d2Q4 = q4.secondary?.employment_income_items || [];

  // Map year_indicator -> slot index (1=current, 2=previous, 3=two_years)
  const yearSlot = (s) => {
    if (!s) return null;
    const t = String(s).toLowerCase();
    if (t === 'current') return 1;
    if (t === 'previous' || t === 'last') return 2;
    if (t === 'two_years' || t === 'two_years_ago' || t === 'two-years' || t === 'two-years-ago') return 3;
    return null;
  };

  // Helper to accumulate amounts per slot for each debtor (the official form shows a single gross number per year)
  const sumBySlot = (items) => {
    const out = {1: 0, 2: 0, 3: 0};
    for (const it of items) {
      const slot = yearSlot(it.year_indicator);
      if (!slot) continue;
      const amt = Number(it.amount || 0) || 0;
      out[slot] += amt;
    }
    return out;
  };

  const d1Sums = sumBySlot(d1Q4);
  const d2Sums = sumBySlot(d2Q4);

  // Check the source boxes for each year slot
  const checkSources = (items, debtorLabel) => {
    // collect flags by slot
    const flags = {1: {w:false,b:false}, 2: {w:false,b:false}, 3: {w:false,b:false}};
    for (const it of items) {
      const slot = yearSlot(it.year_indicator);
      if (!slot) continue;
      if (it.employment_income) flags[slot].w = true;
      if (it.business_income) flags[slot].b = true;
    }
    for (const i of [1,2,3]) {
      if (flags[i].w) tryCheck2(`wages_yes_${i} ${debtorLabel}`);
      if (flags[i].b) tryCheck2(`business_yes_${i} ${debtorLabel}`);
    }
  };

  checkSources(d1Q4, 'Debtor 1');
  checkSources(d2Q4, 'Debtor 2');

  // Overall employment income yes/no (if any wages or business income for either debtor across any year)
  const hasEmploymentIncome = [...d1Q4, ...d2Q4].some(it => (it && (it.employment_income || it.business_income)));
  if (hasEmploymentIncome) {
    ['employment_income_yes','Employment Income Yes','employment_yes'].forEach(tryCheck2);
  } else {
    ['employment_income_no','Employment Income No','employment_no'].forEach(tryCheck2);
  }

  // Fill gross income amounts
  trySet2('income_1 Debtor 1', d1Sums[1] ? String(d1Sums[1].toFixed ? d1Sums[1].toFixed(2) : d1Sums[1]) : '');
  trySet2('income_2 Debtor 1', d1Sums[2] ? String(d1Sums[2].toFixed ? d1Sums[2].toFixed(2) : d1Sums[2]) : '');
  trySet2('income_3 Debtor 1', d1Sums[3] ? String(d1Sums[3].toFixed ? d1Sums[3].toFixed(2) : d1Sums[3]) : '');

  trySet2('income_1 Debtor 2', d2Sums[1] ? String(d2Sums[1].toFixed ? d2Sums[1].toFixed(2) : d2Sums[1]) : '');
  trySet2('income_2 Debtor 2', d2Sums[2] ? String(d2Sums[2].toFixed ? d2Sums[2].toFixed(2) : d2Sums[2]) : '');
  trySet2('income_3 Debtor 2', d2Sums[3] ? String(d2Sums[3].toFixed ? d2Sums[3].toFixed(2) : d2Sums[3]) : '');

  // Fill previous year labels on the official form
  // page has "previous_year" and "two_years_ago" text fields (from the field dump)
  const now = new Date();
  const lastYear = now.getFullYear() - 1;
  const twoYearsAgo = now.getFullYear() - 2;
  trySet2('previous_year', String(lastYear));
  trySet2('two_years_ago', String(twoYearsAgo));

  // ===== Q5: Other income sources (fill up to 3 rows per year per debtor) =====
  const q5 = data.sofa?.q5 || {};
  const d1Q5 = q5.primary?.other_income_items || [];
  const d2Q5 = q5.secondary?.other_income_items || [];

  const byYear = (items) => {
    const buckets = {1: [], 2: [], 3: []};
    for (const it of items) {
      const slot = yearSlot(it.year_indicator);
      if (!slot) continue;
      buckets[slot].push(it);
    }
    return buckets;
  };

  const d1Buckets = byYear(d1Q5);
  const d2Buckets = byYear(d2Q5);

  // Mark the Q5 yes/no checkbox depending on any entries
  const hasQ5 = (d1Q5.length + d2Q5.length) > 0;
  if (hasQ5) { tryCheck2('other_income_yes'); } else { tryCheck2('other_income_no'); }

  // Fill the 3 slots shown on the official page
  const fillQ5Rows = (buckets, debtorLabel) => {
    const fieldName = (slot, idx, type) => {
      // Example names from field dump:
      // "Income source 1_1 Debtor 1" ... "Income amount 1_1 Debtor 1"
      return `${type} ${slot}_${idx} ${debtorLabel}`;
    };
    for (const slot of [1,2,3]) {
      const rows = buckets[slot].slice(0,3);
      for (let i=0;i<3;i++) {
        const it = rows[i];
        trySet2(fieldName(slot, i+1, 'Income source'), it?.description || '');
        trySet2(fieldName(slot, i+1, 'Income amount'), it?.amount ? String(Number(it.amount).toFixed(2)) : '');
      }
    }
  };

  fillQ5Rows(d1Buckets, 'Debtor 1');
  fillQ5Rows(d2Buckets, 'Debtor 2');

  // Move unchecked checkboxes off-page and flatten
  moveUncheckedCheckboxesToCorner(form2, pageSize2, sofaPdfClone2.context);
  form2.flatten();

  const tmpBytes2 = await sofaPdfClone2.save();
  const tmpDoc2 = await PDFDocument.load(tmpBytes2);
  const [filledP2] = await out.copyPages(tmpDoc2, [1]); // second page
  out.addPage(filledP2);
} catch (e) {
  console.warn('Falling back to blank Page 2 due to fill error:', e);
  const [blankP2] = await out.copyPages(sofaPdf, [1]);
  out.addPage(blankP2);
}

// 3.6) Q5 Continuation Pages — if any year has more than 3 entries for either debtor
{
  const q5 = data.sofa?.q5 || {};
  const d1Q5 = q5.primary?.other_income_items || [];
  const d2Q5 = q5.secondary?.other_income_items || [];

  const yearSlot = (s) => {
    if (!s) return null;
    const t = String(s).toLowerCase();
    if (t === 'current') return 1;
    if (t === 'previous' || t === 'last') return 2;
    if (t === 'two_years' || t === 'two_years_ago' || t === 'two-years' || t === 'two-years-ago') return 3;
    return null;
  };

  const byYear = (items) => {
    const buckets = {1: [], 2: [], 3: []};
    for (const it of items) {
      const slot = yearSlot(it.year_indicator);
      if (!slot) continue;
      buckets[slot].push(it);
    }
    return buckets;
  };

  const d1B = byYear(d1Q5);
  const d2B = byYear(d2Q5);

  const needs = [1,2,3].some(slot => (d1B[slot].length > 3) || (d2B[slot].length > 3));
  if (needs) {
    const helv = await out.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792];
    const [W, H] = pageSize;

    const yearLabel = (slot) => {
      if (slot === 1) return `YTD ${new Date().getFullYear()}`;
      if (slot === 2) return `${new Date().getFullYear()-1}`;
      return `${new Date().getFullYear()-2}`;
    };

    const toMoney = (n) => {
      const v = Number(n || 0);
      return isFinite(v) ? `$${v.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '';
    };

    // Build a flat list of overflow rows
    const rows = [];
    for (const slot of [1,2,3]) {
      const d1Extra = d1B[slot].slice(3);
      const d2Extra = d2B[slot].slice(3);
      for (const it of d1Extra) {
        rows.push({ year: yearLabel(slot), debtor: 'Debtor 1', source: it.description || '', amount: toMoney(it.amount) });
      }
      for (const it of d2Extra) {
        rows.push({ year: yearLabel(slot), debtor: 'Debtor 2', source: it.description || '', amount: toMoney(it.amount) });
      }
    }

    if (rows.length) {
      // Drawer for a neat table with shaded rows and divider lines
      const drawHeader = (page) => {
        page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: 50, y: H - 50, size: 14, font: helv });
        page.drawText('Part 2, Question 5: Other income (additional sources)', { x: 50, y: H - 68, size: 12, font: helv });

        // Column titles
        const y0 = H - 96;
        page.drawText('Year',   { x: 50,  y: y0, size: 10, font: helv });
        page.drawText('Debtor', { x: 140, y: y0, size: 10, font: helv });
        page.drawText('Source (description)', { x: 220, y: y0, size: 10, font: helv });
        page.drawText('Amount (gross)', { x: 480, y: y0, size: 10, font: helv });

        // Top line
        page.drawLine({ start: {x: 50, y: y0 - 4}, end: {x: W - 50, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });
      };

      const drawRow = (page, y, r, shaded) => {
        // Shaded background
        if (shaded) {
          page.drawRectangle({ x: 48, y: y - 14, width: W - 96, height: 16, color: rgb(0.95,0.95,0.95) });
        }
        // Text
        page.drawText(r.year,   { x: 50,  y: y - 10, size: 10, font: helv });
        page.drawText(r.debtor, { x: 140, y: y - 10, size: 10, font: helv });
        // Wrap source if long
        const maxW = 240;
        const words = String(r.source || '').split(/\s+/);
        let line = '';
        let used = 0;
        const pushLine = (txt, yy) => page.drawText(txt, { x: 220, y: yy, size: 10, font: helv });
        for (const w of words) {
          const test = line ? `${line} ${w}` : w;
          if (helv.widthOfTextAtSize(test, 10) <= maxW) {
            line = test;
          } else {
            pushLine(line, y - 10 - used*12);
            used++;
            line = w;
          }
        }
        if (line) {
          pushLine(line, y - 10 - used*12);
          used++;
        }
        // amount (right-aligned-ish)
        page.drawText(r.amount, { x: 480, y: y - 10, size: 10, font: helv });

        // Divider line under the row block
        const drop = Math.max(16, used*12 + 6);
        page.drawLine({ start: {x: 50, y: y - drop}, end: {x: W - 50, y: y - drop}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        return y - drop - 6;
      };

      let i = 0;
      while (i < rows.length) {
        const page = out.addPage(pageSize);
        drawHeader(page);
        let y = H - 112;
        let shaded = false;
        while (i < rows.length && y > 60) {
          y = drawRow(page, y, rows[i], shaded);
          shaded = !shaded;
          i++;
        }
      }
    }
  }
}

// 3.7) Fill Page 3 (Q6 — Recent payments within the last 160 days)
try {
  const sofaPdfClone3 = await PDFDocument.load(sofaBytes);
  const form3 = sofaPdfClone3.getForm();
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors = data.debtors || data.caption?.debtors || [];
  const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
  const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
  const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  const d1Name = fullName(d1);
  const d2Name = fullName(d2);
  form3.getTextField('case_number').setText(caseNo)
  form3.getTextField('Debtor 1').setText(d1Name)
  form3.getTextField('Debtor 2').setText(d2Name)
  const trySet3 = (name, val) => { try { form3.getTextField(name).setText(val ?? ''); } catch {} };
  const tryCheck3 = (name) => { try { form3.getCheckBox(name).check(); } catch {} };

  // page index 2 is the third page in the official form
  const pageSize3 = sofaPdfClone3.getPages()[2]?.getSize?.() || sofaPdfClone3.getPages()[0].getSize();

  // Helpers for fuzzy field matching (like Page 1)
  const normalizeName3 = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '_');
  const setByCandidates3 = (candidates, val) => {
    if (val == null) val = '';
    const fields = form3.getFields();
    const norms = candidates.map(c => normalizeName3(c));
    fields.forEach(f => {
      const nm = (f.getName ? f.getName() : '').toString();
      const norm = normalizeName3(nm);
      const hit = norms.some(cn => norm === cn || norm.includes(cn));
      if (hit) { try { form3.getTextField(nm).setText(val); } catch {} }
    });
  };
  const checkByCandidates3 = (candidates) => {
    const fields = form3.getFields();
    const norms = candidates.map(c => normalizeName3(c));
    fields.forEach(f => {
      const nm = (f.getName ? f.getName() : '').toString();
      const norm = normalizeName3(nm);
      const hit = norms.some(cn => norm === cn || norm.includes(cn));
      if (hit) { try { form3.getCheckBox(nm).check(); } catch {} }
    });
  };

  // ========= Source data & filtering ====
  const q6 = data.sofa?.q6 || {};
  const allPayments = Array.isArray(q6.recent_payments) ? q6.recent_payments : [];

  // TEMP: widen window for testing; user will tighten later
  const days160Ago = (() => { const d = new Date(); d.setDate(d.getDate() - 160); return d; })();

  const parseISO = (s) => {
    if (!s) return null;
    const [y,m,d] = String(s).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m-1, d);
  };

  // Keep only the last 160 days (by individual payment)
  const recent = allPayments
    .map(r => ({ ...r, _date: parseISO(r.payment_date) }))
    .filter(r => r._date && r._date >= days160Ago);

  // Group recent payments by creditor (same creditor ID). Sum amounts and collect dates.
  // Fallback to name+address only if an ID is not present in the payload.
  const groupPayments = (list) => {
    const map = new Map();
    for (const r of list) {
      const c = r.creditor || {};
      const a = c.address || {};
      const credId = (c.id || c.creditor_id || c.pk || '').toString().trim();
      const key = credId
        ? `ID:${credId}`
        : [
            (c.name || '').trim().toUpperCase(),
            (a.street1 || '').trim().toUpperCase(),
            (a.street2 || '').trim().toUpperCase(),
            (a.city || '').trim().toUpperCase(),
            (a.state || '').trim().toUpperCase(),
            (a.zip_code || '').trim().toUpperCase(),
          ].join('|');
      const current = map.get(key) || {
        creditor: c,
        address: a,
        payments: [],
        total: 0,
        dates: new Set(),
        account_type: c.account_type || ''
      };
      const amt = Number(r.payment || 0) || 0;
      current.payments.push(r);
      current.total += amt;
      if (r.payment_date) current.dates.add(fmtDate(r.payment_date));
      map.set(key, current);
    }
    // sort groups by most recent payment date (desc)
    const groups = Array.from(map.values());
    groups.sort((ga, gb) => {
      const ad = Math.max(...ga.payments.map(p => (parseISO(p.payment_date) || new Date(0)).getTime()));
      const bd = Math.max(...gb.payments.map(p => (parseISO(p.payment_date) || new Date(0)).getTime()));
      return bd - ad;
    });
    return groups;
  };

  const grouped = groupPayments(recent);

  // Check the Yes/No for "any recent payments"
  if (grouped.length > 0) {
    checkByCandidates3(['q6_yes','recent_payments_yes','payments_yes']);
  } else {
    checkByCandidates3(['q6_no','recent_payments_no','payments_no']);
  }

  // Top-of-question checkbox about consumer debts vs something else
  // Use caseData.debt_type if available; fallback to data.meta.debt_type
  const debtType = (caseData?.debt_type || data?.meta?.debt_type || '').toString().toLowerCase();
  if (debtType.includes('consumer')) {
    // Consumer debts
    checkByCandidates3(['q6_consumer_yes','consumer_debts_yes','consumer_yes']);
  } else if (debtType.includes('non') || debtType.includes('business') || debtType.includes('commercial')) {
    // Non-consumer/commercial debts
    checkByCandidates3(['q6_consumer_no','consumer_debts_no','consumer_no','non_consumer_yes']);
  }

  // ===== Slot fillers (three rows on the official page) =====
  const pad = (v) => (v == null ? '' : String(v));
  const money = (v) => {
    const n = Number(v || 0);
    if (!isFinite(n) || n === 0) return '';
    return n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  };

  // Field name helpers (fuzzy; we set by likely variants)
  const setRowText = (rowIdx, label, value) => {
    setByCandidates3([
      `q6_${label}_${rowIdx}`,
      `recent_${label}_${rowIdx}`,
      `${label}_${rowIdx}`,
      `q6 ${label} ${rowIdx}`
    ], value);
  };

  // Helper for "Other (specify)" description field on main page
  const setOtherDesc = (rowIdx, value) => {
    // Try a wide set of likely field names for the "Other (specify)" description on the main page
    setByCandidates3([
      `recent_payment_${rowIdx}_other_description`,
      `recent_payment_other_description_${rowIdx}`,
      `recent_payment_${rowIdx}_other_desc`,
      `recent_payment_other_desc_${rowIdx}`,
      `recent_other_description_${rowIdx}`,
      `recent_other_desc_${rowIdx}`,
      `q6_other_description_${rowIdx}`,
      `q6_other_desc_${rowIdx}`,
      `other_description_${rowIdx}`,
      `other_desc_${rowIdx}`,
      `other specify ${rowIdx}`,
      `other (specify) ${rowIdx}`,
      `other specify_${rowIdx}`,
      `other (specify)_${rowIdx}`
    ], value);
  };

  const firstThree = grouped.slice(0,3);

  firstThree.forEach((g, i) => {
    const row = i + 1;
    const c = g.creditor || {};
    const a = g.address || {};

    // dates: multiple allowed, comma-separated
    const datesJoined = Array.from(g.dates).join(', ');

    setRowText(row, 'account_name', pad(c.name));
    setRowText(row, 'street1', pad(a.street1));
    if (a.street2 && a.street2.trim()) setRowText(row, 'street2', pad(a.street2));
    setRowText(row, 'city', pad(a.city));
    setRowText(row, 'state', pad(a.state));
    setRowText(row, 'zip', pad(a.zip_code));

    // “Dates of payments”
    // setRowText(row, 'payment_date', datesJoined);
    // Fill up to three date fields using template field names
    const datesArr = Array.from(g.dates);
    const d1 = datesArr[0] || '';
    const d2 = datesArr[1] || '';
    const d3 = datesArr[2] || '';
    setByCandidates3([`recent_payment_date_${row}_1`], d1);
    setByCandidates3([`recent_payment_date_${row}_2`], d2);
    setByCandidates3([`recent_payment_date_${row}_3`], d3);

    // Total paid within the 160-day window
    setRowText(row, 'payment_amount', money(g.total));

    // Still owed (if provided)
    const bal = Number(c.balance || 0);
    setRowText(row, 'payment_amount_still_owed', isFinite(bal) && bal > 0 ? money(bal) : '');

    // Account type (e.g., "Personal Loan", "Credit Card", etc.)
    const checkTypeForRow = (rowIdx, typeStr) => {
      const t = (typeStr || '').toString().toLowerCase();
      const checkExact = (fname) => { try { form3.getCheckBox(fname).check(); } catch {} };

      if (/(mortgage|home|house|real)/.test(t)) { checkExact(`recent_payment_${rowIdx}_mortgage`); return; }
      if (/(auto|vehicle)/.test(t))        { checkExact(`recent_payment_${rowIdx}_car`); return; }
      if (/(credit|card|visa|mastercard|amex)/.test(t)) { checkExact(`recent_payment_${rowIdx}_cc`); return; }
      if (/(loan|personal|installment|signature)/.test(t)) { checkExact(`recent_payment_${rowIdx}_loan`); return; }
      if (/(supplier|vendor)/.test(t)) { checkExact(`recent_payment_${rowIdx}_vendor`); return; }
      if (/(medical|doctor|hospital)/.test(t)) { checkExact(`recent_payment_${rowIdx}_other`); setOtherDesc(rowIdx, typeStr); return; }
      if (/(utility|utilities|power|water|gas)/.test(t))  { checkExact(`recent_payment_${rowIdx}_other`); setOtherDesc(rowIdx, typeStr); return; }
      if (/(tax|irs|state)/.test(t)) { checkExact(`recent_payment_${rowIdx}_other`); setOtherDesc(rowIdx, typeStr); return; }

      // Fallback -> Other + description
      checkExact(`recent_payment_${rowIdx}_other`);
      setOtherDesc(rowIdx, typeStr);
    };
    checkTypeForRow(row, g.account_type || c.account_type || '');
  });

  // Move unchecked boxes off-page & flatten, then copy Page 3 into the output
  moveUncheckedCheckboxesToCorner(form3, pageSize3, sofaPdfClone3.context);
  form3.flatten();

  const tmpBytes3 = await sofaPdfClone3.save();
  const tmpDoc3 = await PDFDocument.load(tmpBytes3);
  const [filledP3] = await out.copyPages(tmpDoc3, [2]); // third page
  out.addPage(filledP3);
} catch (e) {
  console.warn('Falling back to blank Page 3 due to fill error:', e);
  const [blankP3] = await out.copyPages(sofaPdf, [2]);
  out.addPage(blankP3);
}

// 3.8) Q6 Continuation Pages — if more than 3 recent payments (grouped by creditor)
{
  const q6 = data.sofa?.q6 || {};
  const allPayments = Array.isArray(q6.recent_payments) ? q6.recent_payments : [];

  // TEMP: widen window for testing; user will tighten later
  const days160Ago = (() => { const d = new Date(); d.setDate(d.getDate() - 160); return d; })();
  const parseISO = (s) => {
    if (!s) return null;
    const [y,m,d] = String(s).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m-1, d);
  };

  const recent = allPayments
    .map(r => ({ ...r, _date: parseISO(r.payment_date) }))
    .filter(r => r._date && r._date >= days160Ago);

  const groupPayments = (list) => {
    const map = new Map();
    for (const r of list) {
      const c = r.creditor || {};
      const a = c.address || {};
      const credId = (c.id || c.creditor_id || c.pk || '').toString().trim();
      const key = credId
        ? `ID:${credId}`
        : [
            (c.name || '').trim().toUpperCase(),
            (a.street1 || '').trim().toUpperCase(),
            (a.street2 || '').trim().toUpperCase(),
            (a.city || '').trim().toUpperCase(),
            (a.state || '').trim().toUpperCase(),
            (a.zip_code || '').trim().toUpperCase(),
          ].join('|');
      const current = map.get(key) || {
        creditor: c,
        address: a,
        payments: [],
        total: 0,
        dates: new Set(),
        account_type: c.account_type || ''
      };
      const amt = Number(r.payment || 0) || 0;
      current.payments.push(r);
      current.total += amt;
      if (r.payment_date) current.dates.add(fmtDate(r.payment_date));
      map.set(key, current);
    }
    const groups = Array.from(map.values());
    groups.sort((ga, gb) => {
      const ad = Math.max(...ga.payments.map(p => (parseISO(p.payment_date) || new Date(0)).getTime()));
      const bd = Math.max(...gb.payments.map(p => (parseISO(p.payment_date) || new Date(0)).getTime()));
      return bd - ad;
    });
    return groups;
  };

  const grouped = groupPayments(recent);

  if (grouped.length > 3) {
    const helv = await out.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792];
    const [W, H] = pageSize;

    const toMoney = (n) => {
      const v = Number(n || 0);
      return isFinite(v) && v !== 0 ? `$${v.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '';
    };

    // Build rows for groups after the first three shown on the official page
    const rows = grouped.slice(3).map(g => {
      const c = g.creditor || {};
      const a = g.address || {};
      const addr = [
        (a.street1 || '').trim(),
        (a.street2 || '').trim(),
        `${(a.city||'')}${a.city ? ', ' : ''}${(a.state||'')}${a.state ? ' ' : ''}${(a.zip_code||'')}`.trim()
      ].filter(Boolean).join('\n');
      const bal = Number(c.balance || 0);
      return {
        dates: Array.from(g.dates).join(', '),
        creditor: (c.name || '').toString(),
        address: addr,
        acctType: (g.account_type || c.account_type || '').toString(),
        amount: toMoney(g.total),
        balance: isFinite(bal) && bal !== 0 ? toMoney(bal) : ''
      };
    });

    // Keep table columns within a safe right margin, but let the decorative
    // shaded rectangles/lines extend ~24px further to the right only.
    const LEFT = 16;
    const RIGHT_TABLE = W - 36;     // original safe right edge for columns
    const RIGHT_DECOR = RIGHT_TABLE + 24; // extend decorations ~24px further

    const drawHeader = (page) => {
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 2, Question 6: Payments within 160 days prior to filing (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });

      // Column titles
      const y0 = H - 96;
      page.drawText('Dates',        { x: LEFT,        y: y0, size: 10, font: helv });
      page.drawText('Creditor',     { x: LEFT + 80,   y: y0, size: 10, font: helv });
      page.drawText('Address',      { x: LEFT + 210,  y: y0, size: 10, font: helv });
      page.drawText('Account Type', { x: RIGHT_TABLE - 150, y: y0, size: 10, font: helv });
      page.drawText('Amount',       { x: RIGHT_TABLE - 70,  y: y0, size: 10, font: helv });
      page.drawText('Balance',      { x: RIGHT_TABLE - 20,  y: y0, size: 10, font: helv });

      // Top line: extend only the divider line to RIGHT_DECOR
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT_DECOR, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });
    };

    const drawRow = (page, y, r, shaded) => {
      const X_DATES = LEFT;
      const X_CRED  = LEFT + 80;
      const X_ADDR  = LEFT + 210;
      const X_TYPE  = RIGHT_TABLE - 150;
      const X_AMT   = RIGHT_TABLE - 70;
      const X_BAL   = RIGHT_TABLE - 20;

      if (shaded) {
        page.drawRectangle({ x: LEFT - 2, y: y - 30, width: (RIGHT_DECOR - (LEFT - 2)), height: 34, color: rgb(0.95,0.95,0.95) });
      }

      page.drawText(r.dates,    { x: X_DATES, y: y - 12, size: 10, font: helv });
      page.drawText(r.creditor, { x: X_CRED,  y: y - 12, size: 10, font: helv });

      // Wrap address (slightly wider than before)
      const wrap = (text, maxW) => {
        const words = String(text || '').split(/\s+/);
        const lines = [];
        let line = '';
        for (const w of words) {
          const test = line ? `${line} ${w}` : w;
          if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
          else { if (line) lines.push(line); line = w; }
        }
        if (line) lines.push(line);
        return lines;
      };
      const addrMaxWidth = Math.max(160, (X_TYPE - X_ADDR) - 10);
      const addrLines = wrap(r.address, addrMaxWidth);
      addrLines.slice(0,3).forEach((ln, i) => {
        page.drawText(ln, { x: X_ADDR, y: y - 12 - i*12, size: 10, font: helv });
      });

      page.drawText(r.acctType, { x: X_TYPE, y: y - 12, size: 10, font: helv });
      page.drawText(r.amount,   { x: X_AMT,  y: y - 12, size: 10, font: helv });
      page.drawText(r.balance,  { x: X_BAL,  y: y - 12, size: 10, font: helv });

      const rowDepth = Math.max(34, 12 + (addrLines.length - 1) * 12 + 8);
      // Divider line spanning full width (decorations only) to RIGHT_DECOR
      page.drawLine({ start: {x: LEFT, y: y - rowDepth}, end: {x: RIGHT_DECOR, y: y - rowDepth}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });

      return y - rowDepth - 6;
    };

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      drawHeader(page);
      let y = H - 112;
      let shaded = false;
      while (i < rows.length && y > 60) {
        y = drawRow(page, y, rows[i], shaded);
        shaded = !shaded;
        i++;
      }
    }
  }
}

// 3.9) Fill Page 4 Q7 & Q8 (Insider payments within 1 year)
try {
  const sofaPdfClone4 = await PDFDocument.load(sofaBytes);
  const form4 = sofaPdfClone4.getForm();
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors = data.debtors || data.caption?.debtors || [];
  const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
  const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
  const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  const d1Name = fullName(d1);
  const d2Name = fullName(d2);
  form4.getTextField('case_number').setText(caseNo)
  form4.getTextField('Debtor 1').setText(d1Name)
  form4.getTextField('Debtor 2').setText(d2Name)
  const trySet4 = (name, val) => { try { form4.getTextField(name).setText(val ?? ''); } catch {} };
  const tryCheck4 = (name) => { try { form4.getCheckBox(name).check(); } catch {} };

  // page index 3 is the fourth page in the official form
  const pageSize4 = sofaPdfClone4.getPages()[3]?.getSize?.() || sofaPdfClone4.getPages()[0].getSize();

  const fmtDateShort = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    if (!y || !m || !d) return '';
    return `${m}/${d}/${y}`;
  };

  const parseISO4 = (s) => {
    if (!s) return null;
    const [y,m,d] = String(s).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m-1, d);
  };

  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
  const oneYearAgo = daysAgo(365);

  // ===== Q7: Payments TO an insider =====
  const q7 = Array.isArray(data.sofa?.q7) ? data.sofa.q7 : [];
  const q7Recent = q7
    .map(row => ({
      ...row,
      transactions: (row.transactions || []).filter(t => parseISO4(t.payment_date) && parseISO4(t.payment_date) >= oneYearAgo)
    }))
    .filter(row => row.transactions.length > 0);

  // Mark yes/no for Q7
  if (q7Recent.length > 0) tryCheck4('insider_payment_yes'); else tryCheck4('insider_payment_no');

  const money4 = (v) => {
    const n = Number(v || 0);
    if (!isFinite(n) || n === 0) return '';
    return n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  };

  // Fill up to 2 entries on the official page
  const q7Top = q7Recent.slice(0,2);
  q7Top.forEach((row, idx) => {
    const i = idx + 1;
    const totalPaid = row.transactions.reduce((s, t) => s + (Number(t.payment_amount || 0) || 0), 0);
    const dates = row.transactions.map(t => fmtDateShort(t.payment_date));

    trySet4(`insider_name_${i}`, row.insider_name || '');
    trySet4(`insider_street1_${i}`, row.street1 || '');
    trySet4(`insider_street2_${i}`, row.street2 || '');
    trySet4(`insider_city_${i}`, (row.city || ''));
    trySet4(`insider_state_${i}`, (row.state || ''));
    trySet4(`insider_zip_${i}`, (row.zip_code || ''));

    // Up to three date widgets
    trySet4(`insider_payment_date_${i}_1`, dates[0] || '');
    trySet4(`insider_payment_date_${i}_2`, dates[1] || '');
    trySet4(`insider_payment_date_${i}_3`, dates[2] || '');

    trySet4(`insider_amount_paid_${i}`, money4(totalPaid));
    trySet4(`insider_amount_owing_${i}`, money4(row.amount_owing));
    trySet4(`insider_reason_${i}`, row.reason_for_payment || '');
  });

  // ===== Q8: Payments ON A DEBT that benefited an insider =====
  const q8 = Array.isArray(data.sofa?.q8) ? data.sofa.q8 : [];
  const q8Recent = q8
    .map(row => ({
      ...row,
      transactions: (row.transactions || []).filter(t => parseISO4(t.payment_date) && parseISO4(t.payment_date) >= oneYearAgo)
    }))
    .filter(row => row.transactions.length > 0);

  // Mark yes/no for Q8 (insider debt payment)
  if (q8Recent.length > 0) tryCheck4('insider_debt_yes'); else tryCheck4('insider_debt_no');

  // Fill up to 2 entries on the official page for Q8
  const q8Top = q8Recent.slice(0,2);
  q8Top.forEach((row, idx) => {
    const i = idx + 1;
    const totalPaid = row.transactions.reduce((s, t) => s + (Number(t.payment_amount || 0) || 0), 0);
    const dates = row.transactions.map(t => fmtDateShort(t.payment_date));

    trySet4(`insider_debt_name_${i}`, row.insider_name || '');
    trySet4(`insider_debt_street1_${i}`, row.street1 || '');
    trySet4(`insider_debt_street2_${i}`, row.street2 || '');
    trySet4(`insider_debt_city_${i}`, (row.city || ''));
    trySet4(`insider_debt_state_${i}`, (row.state || ''));
    trySet4(`insider_debt_zip_${i}`, (row.zip_code || ''));

    // Up to three date widgets
    trySet4(`insider_debt_payment_date_${i}_1`, dates[0] || '');
    trySet4(`insider_debt_payment_date_${i}_2`, dates[1] || '');
    trySet4(`insider_debt_payment_date_${i}_3`, dates[2] || '');

    trySet4(`insider_debt_payment_amount_${i}`, money4(totalPaid));
    trySet4(`insider_debt_payment_amount_owing_${i}`, money4(row.amount_owing));
    // Reason: include creditor/benefited party description if present
    const reason = row.reason_for_payment || '';
    trySet4(`insider_debt_reason_${i}`, reason);
  });

  // Move unchecked checkboxes off-page and flatten
  moveUncheckedCheckboxesToCorner(form4, pageSize4, sofaPdfClone4.context);
  form4.flatten();

  const tmpBytes4 = await sofaPdfClone4.save();
  const tmpDoc4 = await PDFDocument.load(tmpBytes4);
  const [filledP4] = await out.copyPages(tmpDoc4, [3]);
  out.addPage(filledP4);
} catch (e) {
  console.warn('Falling back to blank Page 4 due to fill error:', e);
  const [blankP4] = await out.copyPages(sofaPdf, [3]);
  out.addPage(blankP4);
}

// 3.10) Continuation pages for Q7 and Q8 (more than 2 entries each)
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;

  const fmtDateShort = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    if (!y || !m || !d) return '';
    return `${m}/${d}/${y}`;
  };
  const parseISO4 = (s) => {
    if (!s) return null;
    const [y,m,d] = String(s).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m-1, d);
  };
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
  const oneYearAgo = daysAgo(365);
  const money4 = (v) => {
    const n = Number(v || 0);
    if (!isFinite(n) || n === 0) return '';
    return `$${n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  };

  // Dev switch: pass ?debug_all_insiders=1 in the page URL to build continuation pages
  // from *all* entries (ignoring the 1-year filter). Defaults to recent-only.
  const USE_ALL = queryFlag('debug_all_insiders');

  const drawHeader = (page, title) => {
    page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: 16, y: H - 50, size: 14, font: helv });
    page.drawText(title, { x: 16, y: H - 68, size: 12, font: helv });

    const y0 = H - 96;
    page.drawText('Insider',    { x: 16,  y: y0, size: 10, font: helv });
    page.drawText('Address',    { x: 160, y: y0, size: 10, font: helv });
    page.drawText('Dates',      { x: 360, y: y0, size: 10, font: helv });
    page.drawText('Total Paid', { x: 430, y: y0, size: 10, font: helv });
    page.drawText('Amt. Owing', { x: 500, y: y0, size: 10, font: helv });

    // underline the header row
    page.drawLine({
      start: { x: 16, y: y0 - 4 },
      end:   { x: W - 16, y: y0 - 4 },
      thickness: 0.7,
      color: rgb(0.2, 0.2, 0.2),
    });
  };

  const drawRow = (page, y, r, shaded) => {
    // Row padding and baseline positions
    const X_INS   = 16;
    const X_ADDR  = 160;
    const X_DATE  = 360;
    const X_TOT   = 430;
    const X_OWN   = 500;

    // Wrap helpers
    const wrap = (text, maxW) => {
      const words = String(text || '').split(/\s+/);
      const lines = [];
      let line = '';
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
        else { if (line) lines.push(line); line = w; }
      }
      if (line) lines.push(line);
      return lines;
    };

    // First line: Insider / Address / Dates / Totals
    const addrMax = Math.max(180, X_DATE - X_ADDR - 8);
    const addrLines = wrap(r.address, addrMax).slice(0, 3);
    const firstLineDepth = Math.max(14, 12 * addrLines.length); // how tall the first line block is

    // Second line: Reason (label + wrapped text spanning page)
    const reasonLabel = 'Reason:';
    const reasonLabelW = helv.widthOfTextAtSize(reasonLabel, 10) + 6;
    const reasonMax = (W - 16) - (X_INS + reasonLabelW);
    const reasonLines = wrap(r.reason, reasonMax);
    const reasonDepth = Math.max(14, 12 * Math.max(1, reasonLines.length));

    // Total row depth (add a little padding)
    const totalDepth = firstLineDepth + reasonDepth + 12;

    // Shaded background spanning the full row block (no overlap with header underline)
    if (shaded) {
      page.drawRectangle({
        x: 14,
        y: y - totalDepth,
        width: W - 28,
        height: totalDepth,
        color: rgb(0.95, 0.95, 0.95),
      });
    }

    // --- Draw first line content ---
    page.drawText(r.name,  { x: X_INS,  y: y - 10, size: 10, font: helv });
    addrLines.forEach((ln, i) => {
      page.drawText(ln, { x: X_ADDR, y: y - 10 - i * 12, size: 10, font: helv });
    });
    page.drawText(r.dates, { x: X_DATE, y: y - 10, size: 10, font: helv });
    page.drawText(r.total, { x: X_TOT,  y: y - 10, size: 10, font: helv });
    page.drawText(r.owing, { x: X_OWN,  y: y - 10, size: 10, font: helv });

    // --- Draw second line: "Reason:" spanning width ---
    const reasonTopY = y - firstLineDepth - 6;
    page.drawText(reasonLabel, { x: X_INS, y: reasonTopY - 10, size: 10, font: helv });
    reasonLines.forEach((ln, i) => {
      page.drawText(ln, { x: X_INS + reasonLabelW, y: reasonTopY - 10 - i * 12, size: 10, font: helv });
    });

    // Divider line under the whole row block (placed after reason so it never crosses text)
    page.drawLine({
      start: { x: 16, y: y - totalDepth - 4 },
      end:   { x: W - 16, y: y - totalDepth - 4 },
      thickness: 0.4,
      color: rgb(0.8, 0.8, 0.8),
    });

    // Return next y
    return y - totalDepth - 10;
  };

  // Build rows for Q7 overflow
  const q7All = Array.isArray(data.sofa?.q7) ? data.sofa.q7 : [];
  const q7RecentRows = q7All
    .map(row => ({
      ...row,
      transactions: (row.transactions || []).filter(t => parseISO4(t.payment_date) && parseISO4(t.payment_date) >= oneYearAgo)
    }))
    .filter(row => row.transactions.length > 0);
  const q7ForCont = USE_ALL ? q7All.filter(r => (r.transactions || []).length > 0) : q7RecentRows;

  if (q7ForCont.length > 2) {
    const rows = q7ForCont.slice(2).map(row => {
      const tx = USE_ALL ? (row.transactions || []) : row.transactions;
      const total = (tx || []).reduce((s,t)=> s + (Number(t.payment_amount || 0) || 0), 0);
      const dates = (tx || []).map(t => fmtDateShort(t.payment_date)).join(', ');
      const addr = [
        (row.street1 || '').trim(),
        (row.street2 || '').trim(),
        `${(row.city||'')}${row.city ? ', ' : ''}${(row.state||'')}${row.state ? ' ' : ''}${(row.zip_code||'')}`.trim()
      ].filter(Boolean).join(' ');
      return {
        name: row.insider_name || '',
        address: addr,
        dates,
        total: money4(total),
        owing: money4(row.amount_owing),
        reason: row.reason_for_payment || ''
      };
    });

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      drawHeader(page, 'Part 3, Question 7: Payments to an insider (additional entries)');
      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) { y = drawRow(page, y, rows[i], shaded); shaded = !shaded; i++; }
    }
  }

  // Build rows for Q8 overflow
  const q8All = Array.isArray(data.sofa?.q8) ? data.sofa.q8 : [];
  const q8RecentRows = q8All
    .map(row => ({
      ...row,
      transactions: (row.transactions || []).filter(t => parseISO4(t.payment_date) && parseISO4(t.payment_date) >= oneYearAgo)
    }))
    .filter(row => row.transactions.length > 0);
  const q8ForCont = USE_ALL ? q8All.filter(r => (r.transactions || []).length > 0) : q8RecentRows;

  if (q8ForCont.length > 2) {
    const rows = q8ForCont.slice(2).map(row => {
      const tx = USE_ALL ? (row.transactions || []) : row.transactions;
      const total = (tx || []).reduce((s,t)=> s + (Number(t.payment_amount || 0) || 0), 0);
      const dates = (tx || []).map(t => fmtDateShort(t.payment_date)).join(', ');
      const addr = [
        (row.street1 || '').trim(),
        (row.street2 || '').trim(),
        `${(row.city||'')}${row.city ? ', ' : ''}${(row.state||'')}${row.state ? ' ' : ''}${(row.zip_code||'')}`.trim()
      ].filter(Boolean).join(' ');
      return {
        name: row.insider_name || '',
        address: addr,
        dates,
        total: money4(total),
        owing: money4(row.amount_owing),
        reason: row.reason_for_payment || ''
      };
    });

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      drawHeader(page, 'Part 3, Question 8: Payments on a debt that benefited an insider (additional entries)');
      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) { y = drawRow(page, y, rows[i], shaded); shaded = !shaded; i++; }
    }
  }
}

// 3.11) Fill Page 5 (Q9 Lawsuits; Q10 Garnishments/Repos/Seizures)
try {
  const sofaPdfClone5 = await PDFDocument.load(sofaBytes);
  const form5 = sofaPdfClone5.getForm();
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors = data.debtors || data.caption?.debtors || [];
  const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
  const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
  const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  const d1Name = fullName(d1);
  const d2Name = fullName(d2);
  form5.getTextField('case_number').setText(caseNo)
  form5.getTextField('Debtor 1').setText(d1Name)
  form5.getTextField('Debtor 2').setText(d2Name)
  const trySet5 = (name, val) => { try { form5.getTextField(name).setText(val ?? ''); } catch {} };
  const tryCheck5 = (name) => { try { form5.getCheckBox(name).check(); } catch {} };
  const pageSize5 = sofaPdfClone5.getPages()[4]?.getSize?.() || sofaPdfClone5.getPages()[0].getSize();

  const normalize5 = s => (s || '').toString().toLowerCase().replace(/\s+/g, '_');
  const setByCand5 = (cands, val) => {
    const fields = form5.getFields();
    const norms = cands.map(c => normalize5(c));
    fields.forEach(f => {
      const nm = (f.getName ? f.getName() : '').toString();
      const hit = norms.some(nn => normalize5(nm) === nn || normalize5(nm).includes(nn));
      if (hit) { try { form5.getTextField(nm).setText(val ?? ''); } catch {} }
    });
  };
  const checkByCand5 = (cands) => {
    const fields = form5.getFields();
    const norms = cands.map(c => normalize5(c));
    fields.forEach(f => {
      const nm = (f.getName ? f.getName() : '').toString();
      const hit = norms.some(nn => normalize5(nm) === nn || normalize5(nm).includes(nn));
      if (hit) { try { form5.getCheckBox(nm).check(); } catch {} }
    });
  };

  // ===== Q9: Lawsuits within 1 year =====
  const q9 = Array.isArray(data.sofa?.q9) ? data.sofa.q9 : [];
  if (q9.length) checkByCand5(['lawsuit_yes','q9_yes','suits_yes']); else checkByCand5(['lawsuit_no','q9_no','suits_no']);

  const q9Top = q9.slice(0, 2);
  q9Top.forEach((row, idx) => {
    const i = idx + 1;
    const addr = `${row.court_street || ''}`;
    const cityst = `${row.court_city || ''}${row.court_city ? ', ' : ''}${row.court_state || ''} ${row.court_zip || ''}`.trim();

    // --- Lawsuit fields (use the exact field names from the template) ---
    trySet5(`case_title_${i}`, row.case_title || '');
    trySet5(`lawsuit_case_number_${i}`, row.case_number || '');
    // also set generic aliases if they exist
    setByCand5([`q9_case_number_${i}`, `case_number_${i}`], row.case_number || '');

    trySet5(`case_nature_${i}`, row.nature_of_case || '');
    trySet5(`court_name_${i}`, row.court_name || '');
    trySet5(`court_street_${i}`, row.court_street || '');
    trySet5(`court_city_${i}`, row.court_city || '');
    trySet5(`court_state_${i}`, row.court_state || '');
    trySet5(`court_zip_${i}`, row.court_zip || '');
    // status text (if a free-text field exists)
    setByCand5([`lawsuit_status_${i}`, `q9_status_${i}`, `status_${i}`], row.status || '');

    // status checkboxes (pending / concluded / appeal)
    const s = (row.status || '').toString().toLowerCase();
    if (/appeal/.test(s)) {
      tryCheck5(`case_${i}_appeal`);
    } else if (/disposed|concluded|closed|dismissed|final|judg/.test(s)) {
      tryCheck5(`case_${i}_concluded`);
    } else if (/open|pending|active|ongoing|current/.test(s)) {
      tryCheck5(`case_${i}_pending`);
    }
  });

  // ===== Q10: Garnishment / Attachment / Seizure / Repossession (last 1 year) =====
  // (You told me to ignore the strict window during testing earlier; keep your toggle if desired)
  const q10 = Array.isArray(data.sofa?.q10) ? data.sofa.q10 : [];
  if (q10.length) checkByCand5(['garnish_yes','q10_yes','repos_yes','garnishment_yes','repossession_yes','attachment_yes','seizures_yes']);
  else checkByCand5(['garnish_no','q10_no','repos_no','garnishment_no','repossession_no','attachment_no','seizures_no']);

  const money = v => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2}) : '';
  };
  const fmt = s => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };

  const q10Top = q10.slice(0, 2);
  q10Top.forEach((row, idx) => {
    const i = idx + 1;
    const cr = row.creditor || {};
    const a = cr.address || {};
    const t = (row.seizure_type || '').toLowerCase();

    // Toggle the per-row seizure type checkboxes
    if (/garnish/.test(t)) {
      tryCheck5(`garnish_${i}`);
    } else if (/repo|repos/.test(t)) {
      tryCheck5(`repo_${i}`);
    } else if (/foreclos/.test(t)) {
      tryCheck5(`foreclosure_${i}`);
    } else if (/levy/.test(t)) {
      tryCheck5(`levy_${i}`);
    }

    // Always mark the overall section as "yes" when we have any entries
    checkByCand5(['seizures_yes','q10_yes','garnishment_yes','repossession_yes','attachment_yes','seizure_yes']);

    // Fill the exact text fields on the main page
    trySet5(`seized_property_description_${i}`, row.property_description || '');
    trySet5(`seizure_account_name_${i}`, cr.account_name || cr.name || '');
    trySet5(`seized_date_${i}`, fmt(row.garnishment_date));
    trySet5(`seized_property_value_${i}`, money(row.value));

    // Address of seizing/creditor (some templates use a misspelled prefix "seizire_")
    const setAddrVariants = (base, value) => {
      const names = [`seizure_${base}_${i}`, `seizire_${base}_${i}`];
      for (const nm of names) {
        try { form5.getTextField(nm).setText(value ?? ''); } catch {}
      }
    };
    setAddrVariants('street1', cr.street1 || a.street1 || '');
    setAddrVariants('street2', cr.street2 || a.street2 || '');
    setAddrVariants('city',    cr.city    || a.city    || '');
    setAddrVariants('state',   cr.state   || a.state   || '');
    setAddrVariants('zip',     cr.zip_code || a.zip_code || '');
  });

  moveUncheckedCheckboxesToCorner(form5, pageSize5, sofaPdfClone5.context);
  form5.flatten();

  const tmpBytes5 = await sofaPdfClone5.save();
  const tmpDoc5 = await PDFDocument.load(tmpBytes5);
  const [filledP5] = await out.copyPages(tmpDoc5, [4]); // 5th page (0-based index 4)
  out.addPage(filledP5);
} catch (e) {
  console.warn('Falling back to blank Page 5 due to fill error:', e);
  const [blankP5] = await out.copyPages(sofaPdf, [4]);
  out.addPage(blankP5);
}
// 3.12) Continuation pages for Q9 (lawsuits) and Q10 (garnishment/repo)
// [END OF 3.12 BLOCK]

  // === If amended, also generate Envelopes (download as envelopes.pdf) ===
  try {
    const amendedFlag = amendedData?.amended || data?.amended;
    const serviceData = { ...(data || {}), ...(amendedData || {}) };
    const rawCreds = amendmentServiceRecipients(serviceData);
    if (amendedFlag && rawCreds.length) {
      // normalize creditor objects to the shape expected by generateEnvelopesPdf()
      const norm = rawCreds.map(c => {
        const addr = c.address || {};
        return {
          name: c.name || c.account_name || c.creditor_name || addr.name || 'Unknown',
          street1: c.street1 || addr.street1 || '',
          street2: c.street2 || addr.street2 || '',
          city: c.city || addr.city || '',
          state: c.state || addr.state || '',
          zip_code: c.zip_code || addr.zip_code || '',
        };
      }).filter(c => c.name);

      if (norm.length) {
        await generateEnvelopesPdf(norm);
      }
    }
  } catch (e) {
    console.warn('Envelope generation skipped due to error:', e);
  }

{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;

  const LEFT = 16;
  const RIGHT = W - 16;

  const header = (page, title, cols) => {
    page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
    page.drawText(title, { x: LEFT, y: H - 68, size: 12, font: helv });

    const y0 = H - 96;
    let x = LEFT;
    cols.forEach(c => { page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv }); });
    page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });
  };

  const wrap = (text, maxW) => {
    const words = String(text || '').split(/\s+/);
    const lines = [];
    let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
      else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  };

  // ---------- Q9 continuation ----------
  const q9All = Array.isArray(data.sofa?.q9) ? data.sofa.q9 : [];
  if (q9All.length > 2) {
    const rows = q9All.slice(2).map(r => ({
      title: r.case_title || '',
      number: r.case_number || '',
      nature: r.nature_of_case || '',
      court:  r.court_name || '',
      addr:   [r.court_street || '', `${r.court_city || ''}${r.court_city ? ', ' : ''}${r.court_state || ''} ${r.court_zip || ''}`.trim()].filter(Boolean).join(' '),
      status: r.status || ''
    }));

    // Define a fixed layout with widths and word-wrapping so nothing overlaps
    const COLS = [
      { key: 'title',  label: 'Case title', x: LEFT,           w: 160, wrap: true },
      { key: 'number', label: 'Case No.',   x: LEFT + 155,     w: 170, wrap: true },
      { key: 'nature', label: 'Nature',     x: LEFT + 265,     w: 70,  wrap: true },
      // Court column widened and shifted left a bit
      { key: 'court',  label: 'Court',      x: LEFT + 355,     w: 170, wrap: true },
      // Status anchored to the far right
      { key: 'status', label: 'Status',     x: RIGHT - 50,     w: 40,  wrap: false },
    ];

    const drawHead = (page) => {
      header(page, 'Part 3, Question 9: Lawsuits (additional entries)', COLS);
    };

    const wrapCol = (text, width) => {
      const words = String(text || '').split(/\s+/);
      const lines = [];
      let line = '';
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (helv.widthOfTextAtSize(test, 10) <= width) line = test;
        else { if (line) lines.push(line); line = w; }
      }
      if (line) lines.push(line);
      return lines;
    };

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      drawHead(page);
      let y = H - 112;
      let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        // compute wrapped lines and row height
        const wrapped = {};
        let maxLines = 1;
        for (const c of COLS) {
          const v = r[c.key] ?? '';
          wrapped[c.key] = c.wrap ? wrapCol(v, c.w) : [String(v)];
          maxLines = Math.max(maxLines, wrapped[c.key].length);
        }
        const rowHeight = 14 * maxLines + 8;

        if (shaded) {
          page.drawRectangle({ x: LEFT - 2, y: y - rowHeight + 2, width: RIGHT - (LEFT - 2), height: rowHeight, color: rgb(0.95,0.95,0.95) });
        }

        for (let line = 0; line < maxLines; line++) {
          for (const c of COLS) {
            const txt = wrapped[c.key][line] || '';
            page.drawText(txt, { x: c.x, y: y - 12 - line * 14, size: 10, font: helv });
          }
        }

        // "Court address:" band placed beneath the row, confined strictly to the Court column area
        const addrLabel = 'Court address: ';
        const labelWidth = helv.widthOfTextAtSize(addrLabel, 10) + 6;
        const courtColX = COLS[3].x;            // Court column X
        const courtColW = COLS[3].w;            // Court column width
        const addrMaxW  = Math.max(120, courtColW - labelWidth - 6); // keep inside Court column
        const addrLines = wrapCol(r.addr, addrMaxW);
        let addrDrop = 0;
        // label
        page.drawText(addrLabel, { x: courtColX, y: y - rowHeight - 12, size: 10, font: helv });
        // wrapped address inside Court column only
        addrLines.slice(0, 3).forEach((ln, k) => {
          page.drawText(ln, { x: courtColX + labelWidth, y: y - rowHeight - 12 - k*12, size: 10, font: helv });
          addrDrop += 12;
        });

        const totalDepth = rowHeight + addrDrop + 6;
        // divider line after calculating totalDepth so it uses updated addrDrop
        page.drawLine({ start: {x: LEFT, y: y - totalDepth}, end: {x: RIGHT, y: y - totalDepth}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (totalDepth + 6);
        shaded = !shaded;
        i++;
      }
    }
  }

  // ---------- Q10 continuation ----------
  const q10All = Array.isArray(data.sofa?.q10) ? data.sofa.q10 : [];
  if (q10All.length > 2) {
    const money = v => {
      const n = Number(v || 0);
      return isFinite(n) && n !== 0 ? `$${n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2})}` : '';
    };
    const fmt = s => {
      if (!s) return '';
      const [y,m,d] = String(s).split('-');
      return (y && m && d) ? `${m}/${d}/${y}` : '';
    };

    const rows = q10All.slice(2).map(r => {
      const cr = r.creditor || {};
      const addr = [
        (cr.street1 || (cr.address && cr.address.street1) || '') ,
        `${(cr.city || (cr.address && cr.address.city) || '')}${(cr.city || (cr.address && cr.address.city)) ? ', ' : ''}${(cr.state || (cr.address && cr.address.state) || '')} ${(cr.zip_code || (cr.address && cr.address.zip_code) || '')}`.trim()
      ].filter(Boolean).join(' ');
      return {
        date: fmt(r.garnishment_date),
        type: (r.seizure_type || '').toString(),
        property: r.property_description || '',
        creditor: cr.account_name || '',
        address: addr,
        value: money(r.value)
      };
    });

    const COLS = [
      { key: 'date',     label: 'Date',      x: LEFT },
      { key: 'type',     label: 'Type',      x: LEFT + 80 },
      { key: 'property', label: 'Property',  x: LEFT + 170 },
      // Move Creditor further left for more space
      { key: 'creditor', label: 'Creditor',  x: LEFT + 290 },
      { key: 'value',    label: 'Value',     x: RIGHT - 80 },
    ];

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      header(page, 'Part 3, Question 10: Garnishment/Repossession/Seizure (additional entries)', COLS);
      let y = H - 112;
      let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - 40, width: RIGHT - (LEFT - 2), height: 44, color: rgb(0.95,0.95,0.95) });

        page.drawText(r.date,     { x: COLS[0].x, y: y - 12, size: 10, font: helv });
        page.drawText(r.type,     { x: COLS[1].x, y: y - 12, size: 10, font: helv });
        page.drawText(r.property, { x: COLS[2].x, y: y - 12, size: 10, font: helv });
        page.drawText(r.creditor, { x: COLS[3].x, y: y - 12, size: 10, font: helv });

        // Address now appears under Creditor column (not Property)
        const addrWrapMax = Math.max(180, COLS[4].x - COLS[3].x - 24);
        const addrLines = wrap(r.address, addrWrapMax);
        addrLines.slice(0, 2).forEach((ln, k) => {
          page.drawText(ln, { x: COLS[3].x, y: y - 24 - k*12, size: 10, font: helv });
        });

        page.drawText(r.value, { x: COLS[4].x, y: y - 12, size: 10, font: helv });

        const depth = Math.max(44, 24 + Math.max(0, addrLines.length - 1) * 12 + 10);
        page.drawLine({ start: {x: LEFT, y: y - depth}, end: {x: RIGHT, y: y - depth}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (depth + 6);
        shaded = !shaded;
        i++;
      }
    }
  }
}
// 3.13) Fill Page 6 (Q11 Setoffs; Q12 Property held by a custodian; Q13 Gifts)
try {
  const sofaPdfClone6 = await PDFDocument.load(sofaBytes);
  const form6 = sofaPdfClone6.getForm();
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors = data.debtors || data.caption?.debtors || [];
  const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
  const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
  const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  const d1Name = fullName(d1);
  const d2Name = fullName(d2);

  form6.getTextField('case_number').setText(caseNo);
  form6.getTextField('Debtor 1').setText(d1Name);
  form6.getTextField('Debtor 2').setText(d2Name);

  const trySet6  = (n, v) => { try { form6.getTextField(n).setText(v ?? ''); } catch {} };
  const tryCheck6 = (n) => { try { form6.getCheckBox(n).check(); } catch {} };
  const pageSize6 = sofaPdfClone6.getPages()[5]?.getSize?.() || sofaPdfClone6.getPages()[0].getSize();

  const fmtShort = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };
  const money6 = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2}) : '';
  };

  // ===== Q11: Setoffs in the last 90 days (we'll just render the payload we receive) =====
  const q11 = Array.isArray(data.sofa?.q11) ? data.sofa.q11 : [];
  if (q11.length) { tryCheck6('setoff_yes'); } else { tryCheck6('setoff_no'); }

  if (q11[0]) {
    const r = q11[0];
    const cr = r.creditor || {};
    const a = cr.address || {};
    trySet6('setoff_account_name', cr.account_name || cr.name || '');
    trySet6('setoff_description', r.description || '');
    trySet6('setoff_date', fmtShort(r.setoff_date));
    trySet6('setoff_amount', money6(r.amount));
    trySet6('setoff_street1', cr.street1 || a.street1 || '');
    trySet6('setoff_street2', cr.street2 || a.street2 || '');
    trySet6('setoff_city',    cr.city    || a.city    || '');
    trySet6('setoff_state',   cr.state   || a.state   || '');
    trySet6('setoff_zip',     cr.zip_code|| a.zip_code|| '');
    trySet6('setoff_account_number', cr.account_number || '');
  }

  // ===== Q12: Property in possession of a custodian =====
  const q12 = data.sofa?.q12 || {};
  const _cf = q12?.property_in_possession_of_receiver;
  const custodianYes = typeof _cf === 'string'
    ? /^(1|true|yes)$/i.test(_cf)
    : Boolean(_cf);
  if (custodianYes) {
    tryCheck6('custodian_yes');
  } else {
    tryCheck6('custodian_no');
  }

  // ===== Q13: Gifts within 1 year =====
  const q13 = Array.isArray(data.sofa?.q13) ? data.sofa.q13 : [];
  if (q13.length) { tryCheck6('gift_yes'); } else { tryCheck6('gift_no'); }

  // Helper — sum and collect dates for a gift row
  const summarizeGift = (row) => {
    const txs = row.transactions || [];
    const total = txs.reduce((s,t)=> s + (Number(t.transaction_value || 0) || 0), 0);
    const dates = txs.map(t => fmtShort(t.transaction_date)).filter(Boolean);
    return { total, dates };
  };

  const gTop = q13.slice(0,2);
  gTop.forEach((row, idx) => {
    const i = idx + 1;
    const { total, dates } = summarizeGift(row);
    trySet6(`gift_description_${i}`, row.gift_description || '');
    trySet6(`gift_person_${i}`, row.person_name || '');
    trySet6(`gift_date_${i}`, dates[0] || '');
    trySet6(`gift_amount_${i}`, money6(total));
    trySet6(`gift_street1_${i}`, row.street1 || '');
    trySet6(`gift_street2_${i}`, row.street2 || '');
    trySet6(`gift_city_${i}`, (row.city || ''));
    trySet6(`gift_state_${i}`, (row.state || ''));
    trySet6(`gift_zip_${i}`, (row.zip_code || ''));
    trySet6(`gift_relation_${i}`, row.relationship || '');
  });

  // Move stray/unchecked boxes off-page and flatten
  moveUncheckedCheckboxesToCorner(form6, pageSize6, sofaPdfClone6.context);
  form6.flatten();
  const tmpBytes6 = await sofaPdfClone6.save();
  const tmpDoc6 = await PDFDocument.load(tmpBytes6);
  const [filledP6] = await out.copyPages(tmpDoc6, [5]); // sixth page (0-based index 5)
  out.addPage(filledP6);
} catch (e) {
  console.warn('Falling back to blank Page 6 due to fill error:', e);
  const [blankP6] = await out.copyPages(sofaPdf, [5]);
  out.addPage(blankP6);
}

// 3.14) Continuation pages for Q11 (if more than 1) and Q13 (if more than 2)
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;
  const LEFT = 16;
  const RIGHT = W - 16;

  const fmtShort = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };
  const money = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? `$${n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2})}` : '';
  };

  const drawHeader = (page, title, cols) => {
    page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
    page.drawText(title, { x: LEFT, y: H - 68, size: 12, font: helv });
    const y0 = H - 96;
    cols.forEach(c => page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv }));
    page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });
  };

  const wrap = (text, maxW) => {
    const words = String(text || '').split(/\s+/);
    const lines = [];
    let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
      else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  };

  // ----- Q11 continuation (more than 1 setoff) -----
  const q11All = Array.isArray(data.sofa?.q11) ? data.sofa.q11 : [];
  if (q11All.length > 1) {
    const rows = q11All.slice(1).map(r => {
      const cr = r.creditor || {};
      const a = cr.address || {};
      const addr = [
        (cr.street1 || a.street1 || ''),
        (cr.street2 || a.street2 || ''),
        `${(cr.city || a.city || '')}${(cr.city || a.city) ? ', ' : ''}${(cr.state || a.state || '')} ${(cr.zip_code || a.zip_code || '')}`.trim()
      ].filter(Boolean).join(' ');
      return {
        name: cr.account_name || cr.name || '',
        account: cr.account_number || '',
        address: addr,
        date: fmtShort(r.setoff_date),
        amount: money(r.amount),
        desc: r.description || ''
      };
    });

    const COLS = [
      { key: 'name',    label: 'Account/Name', x: LEFT,        w: 150 },
      { key: 'address', label: 'Address',      x: LEFT + 160,  w: 220 },
      { key: 'date',    label: 'Date',         x: LEFT + 390,  w: 70  },
      { key: 'amount',  label: 'Amount',       x: LEFT + 465,  w: 70  },
      { key: 'account', label: 'Acct #',       x: RIGHT - 60,  w: 50  },
    ];

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      drawHeader(page, 'Part 3, Question 11: Setoffs (additional entries)', COLS);
      let y = H - 112;
      let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const addrLines = wrap(r.address, 210);
        const rowHeight = Math.max(20, 12 * addrLines.length + 8);

        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowHeight - 6, width: RIGHT - (LEFT - 2), height: rowHeight + 10, color: rgb(0.95,0.95,0.95) });

        page.drawText(r.name,   { x: COLS[0].x, y: y - 12, size: 10, font: helv });
        addrLines.forEach((ln, k) => page.drawText(ln, { x: COLS[1].x, y: y - 12 - k*12, size: 10, font: helv }));
        page.drawText(r.date,   { x: COLS[2].x, y: y - 12, size: 10, font: helv });
        page.drawText(r.amount, { x: COLS[3].x, y: y - 12, size: 10, font: helv });
        page.drawText(r.account,{ x: COLS[4].x, y: y - 12, size: 10, font: helv });

        const drop = rowHeight + 12;
        page.drawLine({ start: {x: LEFT, y: y - drop}, end: {x: RIGHT, y: y - drop}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (drop + 6);
        shaded = !shaded;
        i++;
      }
    }
  }

  // ----- Q13 continuation (more than 2 gifts) -----
  const q13All = Array.isArray(data.sofa?.q13) ? data.sofa.q13 : [];
  if (q13All.length > 2) {
    const summarize = (row) => {
      const txs = row.transactions || [];
      const total = txs.reduce((s,t)=> s + (Number(t.transaction_value || 0) || 0), 0);
      const dates = txs.map(t => fmtShort(t.transaction_date)).filter(Boolean).join(', ');
      const addr = [
        (row.street1 || ''),
        (row.street2 || ''),
        `${(row.city||'')}${row.city ? ', ' : ''}${(row.state||'')} ${(row.zip_code||'')}`.trim()
      ].filter(Boolean).join(' ');
      return { dates, total: money(total), addr };
    };

    const rows = q13All.slice(2).map(r => {
      const s = summarize(r);
      return {
        person: r.person_name || '',
        relation: r.relationship || '',
        address: s.addr,
        dates: s.dates,
        amount: s.total,
        desc: r.gift_description || '',
      };
    });

    const COLS = [
      { key: 'person',  label: 'Person',  x: LEFT,        w: 150 },
      { key: 'relation',label: 'Relation',x: LEFT + 155,  w: 70  },
      { key: 'address', label: 'Address', x: LEFT + 230,  w: 210 },
      { key: 'dates',   label: 'Dates',   x: LEFT + 445,  w: 80  },
      { key: 'amount',  label: 'Amount',  x: RIGHT - 60,  w: 50  },
    ];

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      drawHeader(page, 'Part 3, Question 13: Gifts (additional entries)', COLS);
      let y = H - 112;
      let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const addrLines = wrap(r.address, 210);
        const descLines = wrap(r.desc, RIGHT - LEFT - 20);
        const rowHeight = Math.max(20, 12 * Math.max(1, addrLines.length));

        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowHeight - 6 - (descLines.length ? (descLines.length*12 + 10) : 0), width: RIGHT - (LEFT - 2), height: rowHeight + (descLines.length ? (descLines.length*12 + 10) : 0) + 10, color: rgb(0.95,0.95,0.95) });

        page.drawText(r.person,   { x: COLS[0].x, y: y - 12, size: 10, font: helv });
        page.drawText(r.relation, { x: COLS[1].x, y: y - 12, size: 10, font: helv });
        addrLines.forEach((ln, k) => page.drawText(ln, { x: COLS[2].x, y: y - 12 - k*12, size: 10, font: helv }));
        page.drawText(r.dates,    { x: COLS[3].x, y: y - 12, size: 10, font: helv });
        page.drawText(r.amount,   { x: COLS[4].x, y: y - 12, size: 10, font: helv });

        // Description band below the row
        let y2 = y - rowHeight - 8;
        if (descLines.length) {
          const label = 'Description:';
          const labelW = helv.widthOfTextAtSize(label, 10) + 6;
          page.drawText(label, { x: LEFT, y: y2 - 10, size: 10, font: helv });
          descLines.forEach((ln, k) => page.drawText(ln, { x: LEFT + labelW, y: y2 - 10 - k*12, size: 10, font: helv }));
          y2 -= (descLines.length*12 + 12);
        }

        page.drawLine({ start: {x: LEFT, y: y2 - 6}, end: {x: RIGHT, y: y2 - 6}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y = y2 - 10;
        shaded = !shaded;
        i++;
      }
    }
  }
}


// 3.15) Fill Page 7 (Q14 Charitable gifts; Q15 Losses; Q16 Payments to a bankruptcy attorney)
try {
  const sofaPdfClone7 = await PDFDocument.load(sofaBytes);
  const form7 = sofaPdfClone7.getForm();
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors = data.debtors || data.caption?.debtors || [];
  const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
  const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
  const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  const d1Name = fullName(d1);
  const d2Name = fullName(d2);
  // header fields
  try { form7.getTextField('case_number').setText(caseNo); } catch {}
  try { form7.getTextField('Debtor 1').setText(d1Name); } catch {}
  try { form7.getTextField('Debtor 2').setText(d2Name); } catch {}

  const trySet7  = (n, v) => { try { form7.getTextField(n).setText(v ?? ''); } catch {} };
  const tryCheck7 = (n) => { try { form7.getCheckBox(n).check(); } catch {} };
  const pageSize7 = sofaPdfClone7.getPages()[6]?.getSize?.() || sofaPdfClone7.getPages()[0].getSize();

  const fmtShort = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };
  const money7 = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2}) : '';
  };

  // ===== Q14: Charitable contributions within 1 year =====
  const q14 = Array.isArray(data.sofa?.q14) ? data.sofa.q14 : [];
  if (q14.length) { tryCheck7('charity_yes'); } else { tryCheck7('charity_no'); }
  if (q14[0]) {
    const r = q14[0];
    const total = (r.transactions || []).reduce((s,t)=> s + (Number(t.transaction_value||0)||0), 0);
    const firstDate = (r.transactions || [])[0]?.transaction_date || '';
    trySet7('charity_description', r.gift_description || '');
    trySet7('charity_name', r.charity_name || '');
    trySet7('charity_date', fmtShort(firstDate));
    trySet7('charity_amount', money7(total));
    trySet7('charity_street1', r.street1 || '');
    trySet7('charity_street2', r.street2 || '');
    trySet7('charity_city',    r.city    || '');
    trySet7('charity_state',   r.state   || '');
    trySet7('charity_zip',     r.zip_code|| '');
  }

  // ===== Q15: Losses from fire/theft/gambling etc. =====
  const q15 = Array.isArray(data.sofa?.q15) ? data.sofa.q15 : [];
  if (q15.length) { tryCheck7('losses_yes'); } else { tryCheck7('losses_no'); }
  if (q15[0]) {
    const r = q15[0];
    trySet7('losses_description', r.property_loss_description || '');
    trySet7('losses_insurance',   r.insurance_coverage_description || '');
    trySet7('losses_date',        fmtShort(r.loss_date));
    trySet7('losses_amount',      money7(r.property_value_lost));
  }

  // ===== Q16: Payments to person who promised to help file bankruptcy =====
  const q16 = Array.isArray(data.sofa?.q16) ? data.sofa.q16 : [];
  if (q16.length) { tryCheck7('bk_attorney_yes'); } else { tryCheck7('bk_attorney_no'); }

  // Page has room for two entries (name_1 and name_2). Support both if present.
  const fillQ16 = (row, slot) => {
    const totalPaid = Number(row.payment_amount || 0) || 0; // if multiple payments exist in your model, sum here
    trySet7(`bk_attorney_name_${slot}`, row.name || '');
    trySet7(`bk_attorney_services_${slot}`, row.service_description || '');
    trySet7(`bk_attorney_date_1_${slot}`, fmtShort(row.payment_date));
    trySet7(`bk_attorney_amount_1_${slot}`, money7(totalPaid));
    trySet7(`bk_attorney_street1_${slot}`, row.street1 || '');
    trySet7(`bk_attorney_street2_${slot}`, row.street2 || '');
    trySet7(`bk_attorney_city_${slot}`,    row.city    || '');
    trySet7(`bk_attorney_state_${slot}`,   row.state   || '');
    trySet7(`bk_attorney_zip_${slot}`,     row.zip_code|| '');
    trySet7(`bk_attorney_email_${slot}`,   row.email   || '');
    trySet7(`bk_attorney_third_party_payor_${slot}`, row.third_party_payor || '');
  };

  if (q16[0]) fillQ16(q16[0], 1);
  //if (q16[1]) fillQ16(q16[1], 2);

  // finalize Page 7
  moveUncheckedCheckboxesToCorner(form7, pageSize7, sofaPdfClone7.context);
  form7.flatten();
  const tmpBytes7 = await sofaPdfClone7.save();
  const tmpDoc7 = await PDFDocument.load(tmpBytes7);
  const [filledP7] = await out.copyPages(tmpDoc7, [6]); // seventh page (0-based index 6)
  out.addPage(filledP7);
} catch (e) {
  console.warn('Falling back to blank Page 7 due to fill error:', e);
  const [blankP7] = await out.copyPages(sofaPdf, [6]);
  out.addPage(blankP7);
}

// 3.16) Continuation pages for Q14/Q15/Q16 (beyond page capacity)
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;
  const LEFT = 16;
  const RIGHT = W - 16;
  const money = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? `$${n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2})}` : '';
  };
  const fmtShort = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };
  const wrap = (text, maxW) => {
    const words = String(text || '').split(/\s+/);
    const lines = []; let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test; else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  };

  // ----- Q14 continuation (more than 1 charity) -----
  const q14All = Array.isArray(data.sofa?.q14) ? data.sofa.q14 : [];
  if (q14All.length > 1) {
    const rows = q14All.slice(1).map(r => {
      const total = (r.transactions || []).reduce((s,t)=> s + (Number(t.transaction_value||0)||0), 0);
      const dates = (r.transactions || []).map(t => fmtShort(t.transaction_date)).filter(Boolean).join(', ');
      const addr = [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' ');
      return { name: r.charity_name||'', addr, dates, amount: money(total), desc: r.gift_description||'' };
    });

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 4, Question 14: Charitable contributions (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      page.drawText('Charity', { x: LEFT, y: y0, size: 10, font: helv });
      page.drawText('Address', { x: LEFT + 160, y: y0, size: 10, font: helv });
      page.drawText('Dates',   { x: LEFT + 360, y: y0, size: 10, font: helv });
      page.drawText('Amount',  { x: RIGHT - 60, y: y0, size: 10, font: helv });
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const addrLines = wrap(r.addr, 190);
        const descLines = wrap(r.desc, RIGHT - LEFT - 20);
        const rowHeight = Math.max(20, 12 * Math.max(1, addrLines.length));
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowHeight - (descLines.length?descLines.length*12+12:0) - 6, width: RIGHT - (LEFT - 2), height: rowHeight + (descLines.length?descLines.length*12+12:0) + 10, color: rgb(0.95,0.95,0.95) });

        page.drawText(r.name, { x: LEFT, y: y - 12, size: 10, font: helv });
        addrLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + 160, y: y - 12 - k*12, size: 10, font: helv }));
        page.drawText(r.dates,  { x: LEFT + 360, y: y - 12, size: 10, font: helv });
        page.drawText(r.amount, { x: RIGHT - 60, y: y - 12, size: 10, font: helv });

        // description band
        let y2 = y - rowHeight - 8;
        if (descLines.length) {
          const label = 'Description:'; const labelW = helv.widthOfTextAtSize(label, 10) + 6;
          page.drawText(label, { x: LEFT, y: y2 - 10, size: 10, font: helv });
          descLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + labelW, y: y2 - 10 - k*12, size: 10, font: helv }));
          y2 -= (descLines.length*12 + 12);
        }
        page.drawLine({ start: {x: LEFT, y: y2 - 6}, end: {x: RIGHT, y: y2 - 6}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y = y2 - 10; shaded = !shaded; i++;
      }
    }
  }

  // ----- Q15 continuation (more than 1 loss) -----
  const q15All = Array.isArray(data.sofa?.q15) ? data.sofa.q15 : [];
  if (q15All.length > 1) {
    const rows = q15All.slice(1).map(r => ({
      desc: r.property_loss_description || '',
      ins:  r.insurance_coverage_description || '',
      date: fmtShort(r.loss_date),
      amt:  money(r.property_value_lost)
    }));

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 4, Question 15: Losses (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      page.drawText('Description', { x: LEFT, y: y0, size: 10, font: helv });
      page.drawText('Insurance',   { x: LEFT + 270, y: y0, size: 10, font: helv });
      page.drawText('Date',        { x: RIGHT - 120, y: y0, size: 10, font: helv });
      page.drawText('Amount',      { x: RIGHT - 60, y: y0, size: 10, font: helv });
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const dLines = wrap(r.desc, 280);
        const rowH = Math.max(20, 12 * Math.max(1, dLines.length));
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH - 6, width: RIGHT - (LEFT - 2), height: rowH + 10, color: rgb(0.95,0.95,0.95) });
        dLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT, y: y - 12 - k*12, size: 10, font: helv }));
        page.drawText(r.ins, { x: LEFT + 270, y: y - 12, size: 10, font: helv });
        page.drawText(r.date,{ x: RIGHT - 120, y: y - 12, size: 10, font: helv });
        page.drawText(r.amt, { x: RIGHT - 60,  y: y - 12, size: 10, font: helv });
        const drop = rowH + 12; page.drawLine({ start: {x: LEFT, y: y - drop}, end: {x: RIGHT, y: y - drop}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (drop + 6); shaded = !shaded; i++;
      }
    }
  }

  // ----- Q16 continuation (more than 2 entries) -----
  const q16All = Array.isArray(data.sofa?.q16) ? data.sofa.q16 : [];
  if (q16All.length > 1) {
    const rows = q16All.slice(1).map(r => ({
      name: r.name || '',
      service: r.service_description || '',
      date: fmtShort(r.payment_date),
      amt: money(r.payment_amount),
      addr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' '),
      email: r.email || '',
      payor: r.third_party_payor || ''
    }));

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 4, Question 16: Payments related to bankruptcy (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      page.drawText('Name',    { x: LEFT, y: y0, size: 10, font: helv });
      page.drawText('Service', { x: LEFT + 135, y: y0, size: 10, font: helv });
      page.drawText('Address / Email', { x: LEFT + 280, y: y0, size: 10, font: helv });
      page.drawText('Date',    { x: RIGHT - 120, y: y0, size: 10, font: helv });
      page.drawText('Amount',  { x: RIGHT - 60,  y: y0, size: 10, font: helv });
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const svcLines = wrap(r.service, 150);
        const addrBlock = [r.addr, r.email].filter(Boolean).join('\n');
        const addrLines = wrap(addrBlock, 180);
        const rowH = Math.max(20, 12 * Math.max(1, Math.max(svcLines.length, addrLines.length)));
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH - 6, width: RIGHT - (LEFT - 2), height: rowH + 10, color: rgb(0.95,0.95,0.95) });
        page.drawText(r.name, { x: LEFT, y: y - 12, size: 10, font: helv });
        svcLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + 135, y: y - 12 - k*12, size: 10, font: helv }));
        addrLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + 280, y: y - 12 - k*12, size: 10, font: helv }));
        page.drawText(r.date, { x: RIGHT - 120, y: y - 12, size: 10, font: helv });
        page.drawText(r.amt,  { x: RIGHT - 60,  y: y - 12, size: 10, font: helv });
        const drop = rowH + 12; page.drawLine({ start: {x: LEFT, y: y - drop}, end: {x: RIGHT, y: y - drop}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (drop + 6); shaded = !shaded; i++;
      }
    }
  }
}




// 3.17) Fill Page 8 (Q17 Transfers to friends/relatives; Q18 Other transfers outside ordinary course)
try {
  const sofaPdfClone8 = await PDFDocument.load(sofaBytes);
  const form8 = sofaPdfClone8.getForm();
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors = data.debtors || data.caption?.debtors || [];
  const d1 = debtors.find(d => d.is_primary && d.is_filing) || debtors[0] || {};
  const d2 = debtors.find(d => !d.is_primary && d.is_filing) || {};
  const fullName = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  const d1Name = fullName(d1);
  const d2Name = fullName(d2);
  try { form8.getTextField('case_number').setText(caseNo); } catch {}
  try { form8.getTextField('Debtor 1').setText(d1Name); } catch {}
  try { form8.getTextField('Debtor 2').setText(d2Name); } catch {}

  const trySet8  = (n, v) => { try { form8.getTextField(n).setText(v ?? ''); } catch {} };
  const tryCheck8 = (n) => { try { form8.getCheckBox(n).check(); } catch {} };

  // The 8th official page (0-based index 7)
  const pageSize8 = sofaPdfClone8.getPages()[7]?.getSize?.() || sofaPdfClone8.getPages()[0].getSize();

  const fmtShort8 = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };
  const money8 = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2}) : '';
  };

  // ===== Q17: Payments/Transfers to friends or relatives in last 1 year =====
  const q17 = Array.isArray(data.sofa?.q17) ? data.sofa.q17 : [];
  if (q17.length) { tryCheck8('agent_yes'); } else { tryCheck8('agent_no'); }

  if (q17[0]) {
    const r = q17[0];
    trySet8('agent_name', r.person || '');
    trySet8('agent_description', r.transfer_description || '');
    trySet8('agent_street1', r.street1 || '');
    trySet8('agent_street2', r.street2 || '');
    trySet8('agent_city',    r.city    || '');
    trySet8('agent_state',   r.state   || '');
    trySet8('agent_zip',     r.zip_code|| '');
    trySet8('agent_date',    fmtShort8(r.payment_date));
    trySet8('agent_amount',  money8(r.payment_amount));
  }

  // ===== Q18: Transfers outside the ordinary course in last 2 years =====
  const q18 = Array.isArray(data.sofa?.q18) ? data.sofa.q18 : [];
  if (q18.length) { tryCheck8('transfers_yes'); } else { tryCheck8('transfers_no'); }

  const fillQ18 = (row, i) => {
    trySet8(`transfers_name_${i}`, row.person || '');
    trySet8(`transfers_description_${i}`, row.description || '');
    trySet8(`transfers_description_received_${i}`, row.payment_received ? money8(row.payment_received) : '');
    trySet8(`transfers_street1_${i}`, row.street1 || '');
    trySet8(`transfers_street2_${i}`, row.street2 || '');
    trySet8(`transfers_city_${i}`, row.city || '');
    trySet8(`transfers_state_${i}`, row.state || '');
    trySet8(`transfers_zip_${i}`, row.zip_code || '');
    trySet8(`transfers_relation_${i}`, row.relationship || '');
    trySet8(`transfers_date_${i}`, fmtShort8(row.transfer_date));
  };

  if (q18[0]) fillQ18(q18[0], 1);
  if (q18[1]) fillQ18(q18[1], 2);

  // finalize Page 8
  moveUncheckedCheckboxesToCorner(form8, pageSize8, sofaPdfClone8.context);
  form8.flatten();
  const tmpBytes8 = await sofaPdfClone8.save();
  const tmpDoc8 = await PDFDocument.load(tmpBytes8);
  const [filledP8] = await out.copyPages(tmpDoc8, [7]); // eighth page (0-based index 7)
  out.addPage(filledP8);
} catch (e) {
  console.warn('Falling back to blank Page 8 due to fill error:', e);
  const [blankP8] = await out.copyPages(sofaPdf, [7]);
  out.addPage(blankP8);
}

// 3.18) Continuation pages for Q17 (if more than 1) and Q18 (if more than 2)
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;
  const LEFT = 16;
  const RIGHT = W - 16;

  const fmtShort8 = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };
  const money8 = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? `$${n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2})}` : '';
  };
  const wrap8 = (text, maxW, font) => {
    const words = String(text || '').split(/\s+/);
    const lines = []; let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (font.widthOfTextAtSize(test, 10) <= maxW) line = test;
      else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  };

  // ----- Q17 continuation (beyond the single slot) -----
  const q17All = Array.isArray(data.sofa?.q17) ? data.sofa.q17 : [];
  if (q17All.length > 1) {
    const rows = q17All.slice(1).map(r => ({
      name: r.person || '',
      addr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' '),
      date: fmtShort8(r.payment_date),
      amt:  money8(r.payment_amount),
      desc: r.transfer_description || ''
    }));

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 5, Question 17: Payments/transfers to friends or relatives (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });

      const y0 = H - 96;
      page.drawText('Name',    { x: LEFT, y: y0, size: 10, font: helv });
      page.drawText('Address', { x: LEFT + 160, y: y0, size: 10, font: helv });
      page.drawText('Date',    { x: RIGHT - 140, y: y0, size: 10, font: helv });
      page.drawText('Amount',  { x: RIGHT - 80,  y: y0, size: 10, font: helv });
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const addrLines = wrap8(r.addr, RIGHT - (LEFT + 160) - 160, helv);
        const descLines = wrap8(r.desc, RIGHT - LEFT - 20, helv);
        const rowH = Math.max(20, 12 * Math.max(1, addrLines.length));

        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH - (descLines.length?descLines.length*12+12:0) - 6, width: RIGHT - (LEFT - 2), height: rowH + (descLines.length?descLines.length*12+12:0) + 10, color: rgb(0.95,0.95,0.95) });

        page.drawText(r.name, { x: LEFT, y: y - 12, size: 10, font: helv });
        addrLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + 160, y: y - 12 - k*12, size: 10, font: helv }));
        page.drawText(r.date, { x: RIGHT - 140, y: y - 12, size: 10, font: helv });
        page.drawText(r.amt,  { x: RIGHT - 80,  y: y - 12, size: 10, font: helv });

        // Description band
        let y2 = y - rowH - 8;
        if (descLines.length) {
          const label = 'Description:'; const labelW = helv.widthOfTextAtSize(label, 10) + 6;
          page.drawText(label, { x: LEFT, y: y2 - 10, size: 10, font: helv });
          descLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + labelW, y: y2 - 10 - k*12, size: 10, font: helv }));
          y2 -= (descLines.length*12 + 12);
        }

        page.drawLine({ start: {x: LEFT, y: y2 - 6}, end: {x: RIGHT, y: y2 - 6}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y = y2 - 10; shaded = !shaded; i++;
      }
    }
  }

  // ----- Q18 continuation (more than 2 rows) -----
  const q18All = Array.isArray(data.sofa?.q18) ? data.sofa.q18 : [];
  if (q18All.length > 2) {
    const rows = q18All.slice(2).map(r => ({
      name: r.person || '',
      addr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' '),
      rel:  r.relationship || '',
      desc: r.description || '',
      recv: r.payment_received ? money8(r.payment_received) : '',
      date: fmtShort8(r.transfer_date),
    }));

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 5, Question 18: Transfers outside the ordinary course (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });

      const y0 = H - 96;
      page.drawText('Name',        { x: LEFT, y: y0, size: 10, font: helv });
      page.drawText('Relation',    { x: LEFT + 150, y: y0, size: 10, font: helv });
      page.drawText('Address',     { x: LEFT + 230, y: y0, size: 10, font: helv });
      page.drawText('Date',        { x: RIGHT - 150, y: y0, size: 10, font: helv });
      page.drawText('Received',    { x: RIGHT - 90,  y: y0, size: 10, font: helv });
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        // --- WRAPPING for Name and Address columns ---
        const nameMax = 130; // wrap the Name column sooner
        const nameLines = wrap8(r.name, nameMax, helv);
        const addrMax  = Math.max(140, RIGHT - (LEFT + 230) - 180); // wrap Address sooner
        const addrLines = wrap8(r.addr, addrMax, helv);
        const descLines = wrap8(r.desc, RIGHT - LEFT - 20, helv);
        // Row height: max of name/address lines
        const rowH = Math.max(20, 12 * Math.max(1, Math.max(nameLines.length, addrLines.length)));

        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH - (descLines.length?descLines.length*12+12:0) - 6, width: RIGHT - (LEFT - 2), height: rowH + (descLines.length?descLines.length*12+12:0) + 10, color: rgb(0.95,0.95,0.95) });

        // Draw wrapped Name lines
        nameLines.forEach((ln, k) => {
          page.drawText(ln, { x: LEFT, y: y - 12 - k * 12, size: 10, font: helv });
        });
        page.drawText(r.rel,  { x: LEFT + 150, y: y - 12, size: 10, font: helv });
        addrLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + 230, y: y - 12 - k*12, size: 10, font: helv }));
        page.drawText(r.date, { x: RIGHT - 150, y: y - 12, size: 10, font: helv });
        page.drawText(r.recv, { x: RIGHT - 90,  y: y - 12, size: 10, font: helv });

        // Description band
        let y2 = y - rowH - 8;
        if (descLines.length) {
          const label = 'Description:'; const labelW = helv.widthOfTextAtSize(label, 10) + 6;
          page.drawText(label, { x: LEFT, y: y2 - 10, size: 10, font: helv });
          descLines.forEach((ln,k)=> page.drawText(ln, { x: LEFT + labelW, y: y2 - 10 - k*12, size: 10, font: helv }));
          y2 -= (descLines.length*12 + 12);
        }

        page.drawLine({ start: {x: LEFT, y: y2 - 6}, end: {x: RIGHT, y: y2 - 6}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y = y2 - 10; shaded = !shaded; i++;
      }
    }
  }
}
// 3.13) Fill Page 9 (Q19 Trusts; Q20 Closed financial accounts; Q21 Safe-deposit boxes)
try {
  const sofaPdfClone9 = await PDFDocument.load(sofaBytes);
  const form9 = sofaPdfClone9.getForm();
  const caseNo = data.caption?.case_number || data.case_number || '';
  const debtors9 = data.debtors || data.caption?.debtors || [];
  const d1_9 = debtors9.find(d => d.is_primary && d.is_filing) || debtors9[0] || {};
  const d2_9 = debtors9.find(d => !d.is_primary && d.is_filing) || {};
  const fullName9 = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  form9.getTextField('case_number').setText(caseNo);
  form9.getTextField('Debtor 1').setText(fullName9(d1_9));
  form9.getTextField('Debtor 2').setText(fullName9(d2_9));
  const trySet9 = (n,v) => { try { form9.getTextField(n).setText(v ?? ''); } catch {} };
  const tryCheck9 = (n) => { try { form9.getCheckBox(n).check(); } catch {} };
  const pageSize9 = sofaPdfClone9.getPages()[8]?.getSize?.() || sofaPdfClone9.getPages()[0].getSize();

  const money9 = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? n.toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2}) : '';
  };
  const fmt9 = (s) => {
    if (!s) return '';
    const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };

  // ===== Q19: Trusts (on-page capacity: 1) =====
  const q19 = Array.isArray(data.sofa?.q19) ? data.sofa.q19 : [];
  if (q19.length) tryCheck9('trust_yes'); else tryCheck9('trust_no');
  const q19Top = q19.slice(0,1);
  if (q19Top[0]) {
    const r = q19Top[0];
    trySet9('trust_name', r.trust_name || '');
    trySet9('trust_description', r.description || '');
    trySet9('trust_date', fmt9(r.transfer_date));
  }

  // ===== Q20: Closed accounts (on-page capacity: 2) =====
  const q20 = Array.isArray(data.sofa?.q20) ? data.sofa.q20 : [];
  if (q20.length) tryCheck9('closed_accounts_yes'); else tryCheck9('closed_accounts_no');
  const typeCheckForRow = (rowIdx, tStr) => {
    const t = (tStr || '').toString().toLowerCase();
    if (/check/.test(t)) tryCheck9(`closed_accounts_checking_${rowIdx}`);
    else if (/sav/.test(t)) tryCheck9(`closed_accounts_savings_${rowIdx}`);
    else if (/money|mm/.test(t)) tryCheck9(`closed_accounts_mm_${rowIdx}`);
    else if (/broker/.test(t)) tryCheck9(`closed_accounts_brokerage_${rowIdx}`);
    else {
      tryCheck9(`closed_accounts_other_${rowIdx}`);
      trySet9(`closed_accounts_other_description_${rowIdx}`, tStr || '');
    }
  };
  const q20Top = q20.slice(0,2);
  q20Top.forEach((r, idx) => {
    const i = idx + 1;
    trySet9(`closed_accounts_institution_${i}`, r.institution_name || '');
    trySet9(`closed_accounts_number_${i}`, r.account_number || '');
    typeCheckForRow(i, r.account_type);
    trySet9(`closed_accounts_date_${i}`, fmt9(r.date_closed));
    trySet9(`closed_accounts_balance_${i}`, money9(r.last_balance));
    trySet9(`closed_accounts_street1_${i}`, r.street1 || '');
    trySet9(`closed_accounts_street2_${i}`, r.street2 || '');
    trySet9(`closed_accounts_city_${i}`, r.city || '');
    trySet9(`closed_accounts_state_${i}`, r.state || '');
    trySet9(`closed_accounts_zip_${i}`, r.zip_code || '');
  });

  // ===== Q21: Safe-deposit boxes (on-page capacity: 2) =====
  const q21 = Array.isArray(data.sofa?.q21) ? data.sofa.q21 : [];
  if (q21.length) tryCheck9('deposit_yes'); else tryCheck9('deposit_no');
  const stillHaveSetter = (rowIdx, stillHas) => {
    // Some templates only expose "deposit_still_have_no#<idx>" for the 'No' choice
    try {
      if (stillHas === false) form9.getCheckBox(`deposit_still_have_no#${rowIdx-1}`).check();
    } catch {}
    // If a "yes" exists (not in the dump), try to check it as well
    try {
      if (stillHas === true) form9.getCheckBox(`deposit_still_have_yes#${rowIdx-1}`).check();
    } catch {}
  };
  const q21Top = q21.slice(0,2);
  q21Top.forEach((r, idx) => {
    const i = idx + 1;
    trySet9('deposit_institution', r.institution || '');
    trySet9('deposit_contents', r.contents_description || '');
    trySet9('deposit_street1', r.street1 || '');
    trySet9('deposit_street2', r.street2 || '');
    trySet9('deposit_city', r.city || '');
    trySet9('deposit_state', r.state || '');
    trySet9('deposit_zip', r.zip_code || '');
    // Other person with access (first entry if present)
    const acc = Array.isArray(r.others_with_access) ? r.others_with_access[0] : null;
    if (acc) {
      trySet9('deposit_access_name', acc.name || '');
      trySet9('deposit_access_street', acc.street || '');
      trySet9('deposit_access_city', acc.city || '');
      trySet9('deposit_access_state', acc.state || '');
      trySet9('deposit_access_zip', acc.zip || '');
    }
    stillHaveSetter(i, r.still_has === true);
  });

  moveUncheckedCheckboxesToCorner(form9, pageSize9, sofaPdfClone9.context);
  form9.flatten();
  const tmpBytes9 = await sofaPdfClone9.save();
  const tmpDoc9 = await PDFDocument.load(tmpBytes9);
  const [filledP9] = await out.copyPages(tmpDoc9, [8]); // ninth page (0-based index 8)
  out.addPage(filledP9);
} catch (e) {
  console.warn('Falling back to blank Page 9 due to fill error:', e);
  const [blankP9] = await out.copyPages(sofaPdf, [8]);
  out.addPage(blankP9);
}

// 3.14) Continuation pages for Q19/Q20/Q21
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;
  const LEFT = 16, RIGHT = W - 16;

  const wrapC = (text, maxW) => {
    const words = String(text || '').split(/\s+/);
    const lines = []; let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
      else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  };

  const drawTable = (title, cols, rows) => {
    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText(title, { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      cols.forEach(c => page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv }));
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const wrapped = {};
        let maxLines = 1;
        cols.forEach(c => {
          const v = r[c.key] ?? '';
          wrapped[c.key] = c.wrap ? wrapC(v, c.w) : [String(v)];
          maxLines = Math.max(maxLines, wrapped[c.key].length);
        });
        const rowH = 14 * maxLines + 8;
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH + 2, width: RIGHT - (LEFT - 2), height: rowH, color: rgb(0.95,0.95,0.95) });
        for (let ln = 0; ln < maxLines; ln++) {
          cols.forEach(c => {
            const txt = wrapped[c.key][ln] || '';
            page.drawText(txt, { x: c.x, y: y - 12 - ln*14, size: 10, font: helv });
          });
        }
        page.drawLine({ start: {x: LEFT, y: y - rowH}, end: {x: RIGHT, y: y - rowH}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (rowH + 6);
        shaded = !shaded;
        i++;
      }
    }
  };

  // Q19 continuation (more than 1)
  const q19 = Array.isArray(data.sofa?.q19) ? data.sofa.q19 : [];
  if (q19.length > 1) {
    const rows = q19.slice(1).map(r => ({
      name: r.trust_name || '',
      desc: r.description || '',
      date: (() => { const [y,m,d]=(r.transfer_date||'').split('-'); return (y&&m&&d)? `${m}/${d}/${y}`:'' })(),
    }));
    const COLS19 = [
      { key: 'name', label: 'Trust name', x: LEFT,       w: 160, wrap: true },
      { key: 'desc', label: 'Description', x: LEFT + 170, w: 260, wrap: true },
      { key: 'date', label: 'Date',        x: RIGHT - 90, w: 80,  wrap: false },
    ];
    drawTable('Part 4, Question 19: Transfers to a self-settled trust (additional entries)', COLS19, rows);
  }

  // Q20 continuation (more than 2)
  const q20 = Array.isArray(data.sofa?.q20) ? data.sofa.q20 : [];
  if (q20.length > 2) {
    const rows = q20.slice(2).map(r => ({
      inst: r.institution_name || '',
      acct: r.account_number || '',
      type: (r.account_type || '').toString(),
      date: (() => { const [y,m,d]=(r.date_closed||'').split('-'); return (y&&m&&d)? `${m}/${d}/${y}`:'' })(),
      bal:  (() => { const n=Number(r.last_balance||0); return isFinite(n)&&n!==0 ? `$${n.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`:'' })(),
      addr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' '),
    }));
    const COLS20 = [
      { key: 'date', label: 'Closed',   x: LEFT,       w: 80,  wrap: false },
      { key: 'inst', label: 'Institution', x: LEFT+70, w: 150, wrap: true },
      { key: 'acct', label: 'Acct #',   x: LEFT+220,   w: 90,  wrap: true },
      { key: 'type', label: 'Type',     x: LEFT+300,   w: 80,  wrap: true },
      { key: 'bal',  label: 'Balance',  x: RIGHT-90,   w: 80,  wrap: false },
      { key: 'addr', label: 'Address',  x: LEFT+355,   w: RIGHT-(LEFT+440), wrap: true },
    ];
    drawTable('Part 4, Question 20: Closed financial accounts (additional entries)', COLS20, rows);
  }

  // Q21 continuation (more than 2 valid entries)
  const q21All = Array.isArray(data.sofa?.q21) ? data.sofa.q21 : [];
  // Keep only rows that actually contain some meaningful data so we don't
  // Filter out truly empty entries (no institution and no contents and no address)
  const notEmpty = (r) => {
    const hasInst = (r.institution || '').trim().length > 0 || (r.institution_name || '').trim().length > 0;
    const hasCont = (r.contents_description || r.description || r.deposit_contents || '').toString().trim().length > 0;
    const hasAddr = (r.street1 || r.city || r.state || r.zip_code);
    const hasAccess = Array.isArray(r.others_with_access) && r.others_with_access.length > 0;
    return hasInst || hasCont || hasAddr || hasAccess;
  };
  const q21Valid = q21All.filter(notEmpty);

  if (q21Valid.length > 2) {
    const rows = q21Valid.slice(2).map((r) => {
      // Normalize institution/name
      const institution = (r.institution || r.institution_name || '').toString().trim();

      // Address string (single block), allow street2 if present
      const addrParts = [
        (r.street1 || '').toString().trim(),
        (r.street2 || '').toString().trim(),
        `${(r.city || '')}${r.city ? ', ' : ''}${(r.state || '')} ${(r.zip_code || '')}`.trim()
      ].filter(Boolean);
      const address = addrParts.join(' ');

      // Contents description
      const contents = (r.contents_description || r.description || r.deposit_contents || '').toString().trim();

      // Still has? (boolean)
      let still = '';
      if (typeof r.still_has === 'boolean') still = r.still_has ? 'Yes' : 'No';
      else if (typeof r.deposit_still_have === 'boolean') still = r.deposit_still_have ? 'Yes' : 'No';

      // Others with access — print every person with full address if available
      const accessLines = [];
      const access = Array.isArray(r.others_with_access) ? r.others_with_access : [];
      for (const p of access) {
        const name = (p.name || '').toString().trim();
        const aLine = [
          (p.street || '').toString().trim(),
          `${(p.city || '')}${p.city ? ', ' : ''}${(p.state || '')} ${(p.zip || '')}`.trim()
        ].filter(Boolean).join(' ');
        if (name && aLine) accessLines.push(`${name} — ${aLine}`);
        else if (name) accessLines.push(name);
        else if (aLine) accessLines.push(aLine);
      }
      const accessText = accessLines.join('; ');

      return { institution, address, contents, access: accessText, still };
    });

    // Column layout per user spec
    const LEFT = 16;
    const COLS = [
      // key, label, x, width, wrap?
      { key: 'institution', label: 'Institution', x: LEFT,           w: 100, wrap: true },
      { key: 'address',     label: 'Address',     x: LEFT + 110,     w: 130, wrap: true },
      { key: 'contents',    label: 'Contents',    x: LEFT + 250,     w: 150, wrap: true },
      { key: 'access',      label: 'Others with access', x: LEFT + 400, w: (W - 16) - (LEFT + 400) - 60, wrap: true },
      { key: 'still',       label: 'Still have?', x: W - 60,         w: 44,  wrap: false },
    ];

    const wrap = (text, maxW) => {
      const words = String(text || '').split(/\s+/);
      const lines = [];
      let line = '';
      for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
        else { if (line) lines.push(line); line = w; }
      }
      if (line) lines.push(line);
      return lines;
    };

    const drawHeader = (page) => {
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 4, Question 21: Safe deposit boxes / depositories (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      for (const c of COLS) page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv });
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: W - 16, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });
    };

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      drawHeader(page);
      let y = H - 112;
      let shaded = false;

      while (i < rows.length && y > 60) {
        const r = rows[i];
        // Compute wrapped lines per column to get the tallest height
        const wrapped = {};
        let maxLines = 1;
        for (const c of COLS) {
          const val = r[c.key] ?? '';
          wrapped[c.key] = c.wrap ? wrap(val, c.w) : [String(val)];
          maxLines = Math.max(maxLines, wrapped[c.key].length);
        }
        const rowHeight = 14 * maxLines + 8;

        // Shaded row background
        if (shaded) {
          page.drawRectangle({ x: LEFT - 2, y: y - rowHeight + 2, width: (W - 16) - (LEFT - 2), height: rowHeight, color: rgb(0.95,0.95,0.95) });
        }

        // Draw lines per column
        for (let line = 0; line < maxLines; line++) {
          for (const c of COLS) {
            const txt = wrapped[c.key][line] || '';
            page.drawText(txt, { x: c.x, y: y - 12 - line * 14, size: 10, font: helv });
          }
        }

        // Divider line under the row
        page.drawLine({ start: {x: LEFT, y: y - rowHeight}, end: {x: W - 16, y: y - rowHeight}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (rowHeight + 6);
        shaded = !shaded;
        i++;
      }
    }
  }
}

// 3.19) Fill Page 10 (Q22 Storage units; Q23 Property the debtor borrowed/holds for someone else; Q24 Environmental law notices)
try {
  const sofaPdfClone10 = await PDFDocument.load(sofaBytes);
  const form10 = sofaPdfClone10.getForm();
  const caseNo10 = data.caption?.case_number || data.case_number || '';
  const debtors10 = data.debtors || data.caption?.debtors || [];
  const d1_10 = debtors10.find(d => d.is_primary && d.is_filing) || debtors10[0] || {};
  const d2_10 = debtors10.find(d => !d.is_primary && d.is_filing) || {};
  const fullName10 = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  try { form10.getTextField('case_number').setText(caseNo10); } catch {}
  try { form10.getTextField('Debtor 1').setText(fullName10(d1_10)); } catch {}
  try { form10.getTextField('Debtor 2').setText(fullName10(d2_10)); } catch {}

  const trySet10 = (n, v) => { try { form10.getTextField(n).setText(v ?? ''); } catch {} };
  const tryCheck10 = (n) => { try { form10.getCheckBox(n).check(); } catch {} };
  const pageSize10 = sofaPdfClone10.getPages()[9]?.getSize?.() || sofaPdfClone10.getPages()[0].getSize();

  const fmt10 = (s) => {
    if (!s) return ''; const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };
  const money10 = (v) => {
    const n = Number(v || 0);
    return isFinite(n) && n !== 0 ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  };

  // ===== Q22: Storage units / facilities =====
  const q22 = Array.isArray(data.sofa?.q22) ? data.sofa.q22 : [];
  if (q22.length) tryCheck10('storage_yes'); else tryCheck10('storage_no');
  if (q22[0]) {
    const r = q22[0];
    trySet10('storage_institution', r.facility_name || '');
    trySet10('storage_storage_contents', r.contents_description || '');
    trySet10('storage_street1', r.street1 || '');
    trySet10('storage_street2', r.street2 || '');
    trySet10('storage_city', r.city || '');
    trySet10('storage_state', r.state || '');
    trySet10('storage_zip', r.zip_code || '');
    // others with access (first one if present)
    const acc = Array.isArray(r.others_with_access) ? r.others_with_access[0] : null;
    if (acc) {
      trySet10('storage_access_name', acc.name || '');
      trySet10('storage_access_street', acc.street || '');
      trySet10('storage_access_city', acc.city || '');
      trySet10('storage_access_state', acc.state || '');
      trySet10('storage_access_zip', acc.zip || '');
    }
    // still have? use both variants if available
    try {
      if (r.still_has === true) form10.getCheckBox('storage_still_have_yes').check();
      else if (r.still_has === false) form10.getCheckBox('storage_still_have_no').check();
    } catch {}
  }

  // ===== Q23: Property the debtor borrowed/holds for someone else =====
  const q23 = Array.isArray(data.sofa?.q23) ? data.sofa.q23 : [];
  if (q23.length) tryCheck10('borrowed_yes'); else tryCheck10('borrowed_no');
  if (q23[0]) {
    const r = q23[0];
    trySet10('borrowed_name', r.owner_name || '');
    trySet10('borrowed_property_description', r.property_description || '');
    trySet10('borrowed_value', money10(r.value));
    trySet10('borrowed_street1', r.owner_street1 || '');
    trySet10('borrowed_street2', r.owner_street2 || '');
    trySet10('borrowed_city', r.owner_city || '');
    trySet10('borrowed_state', r.owner_state || '');
    trySet10('borrowed_zip', r.owner_zip_code || '');
    // location of the property (separate block on most templates)
    trySet10('borrowed_location_street1', r.property_location_street1 || '');
    trySet10('borrowed_location_street2', r.property_location_street2 || '');
    trySet10('borrowed_location_city', r.property_location_city || '');
    trySet10('borrowed_location_state', r.property_location_state || '');
    trySet10('borrowed_location_zip', r.property_location_zip_code || '');
  }

  // ===== Q24: Environmental law notice/violation =====
  const q24 = Array.isArray(data.sofa?.q24) ? data.sofa.q24 : [];
  if (q24.length) tryCheck10('epa_violation_yes'); else tryCheck10('epa_violation_no');
  if (q24[0]) {
    const r = q24[0];
    trySet10('epa_violation_description', r.environmental_law || '');
    trySet10('epa_violation_site', r.site_name || '');
    trySet10('epa_violation_gov_unit', r.government_unit || '');
    trySet10('epa_violation_date', fmt10(r.notice_date));
    // site address
    trySet10('epa_violation_site_street1', r.street1 || '');
    trySet10('epa_violation_site_street2', r.street2 || '');
    trySet10('epa_violation_site_city', r.city || '');
    trySet10('epa_violation_site_state', r.state || '');
    trySet10('epa_violation_site_zip', r.zip_code || '');
    // government unit address
    trySet10('epa_violation_gov_street', r.gov_unit_street1 || r.gov_unit_street || '');
    trySet10('epa_violation_gov_city', r.gov_unit_city || '');
    trySet10('epa_violation_gov_state', r.gov_unit_state || '');
    trySet10('epa_violation_gov_zip', r.gov_unit_zip || '');
  }

  // finalize Page 10
  moveUncheckedCheckboxesToCorner(form10, pageSize10, sofaPdfClone10.context);
  form10.flatten();
  const tmpBytes10 = await sofaPdfClone10.save();
  const tmpDoc10 = await PDFDocument.load(tmpBytes10);
  const [filledP10] = await out.copyPages(tmpDoc10, [9]); // tenth page (0-based index 9)
  out.addPage(filledP10);
} catch (e) {
  console.warn('Falling back to blank Page 10 due to fill error:', e);
  const [blankP10] = await out.copyPages(sofaPdf, [9]);
  out.addPage(blankP10);
}

// 3.20) Continuation pages for Q22 / Q23 / Q24
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;
  const LEFT = 16, RIGHT = W - 16;

  const wrap = (text, maxW) => {
    const words = String(text || '').split(/\s+/);
    const lines = []; let line = '';
    for (const w of words) { const test = line ? `${line} ${w}` : w; if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test; else { if (line) lines.push(line); line = w; } }
    if (line) lines.push(line); return lines;
  };
  const money = (v) => { const n = Number(v || 0); return isFinite(n) && n !== 0 ? `$${n.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` : ''; };
  const fmt = (s) => { if (!s) return ''; const [y,m,d] = String(s).split('-'); return (y&&m&&d)? `${m}/${d}/${y}`:'' };

  const drawTable = (title, cols, rows) => {
    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText(title, { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      cols.forEach(c => page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv }));
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });
      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const wrapped = {}; let maxLines = 1;
        cols.forEach(c => { const v = r[c.key] ?? ''; wrapped[c.key] = c.wrap ? wrap(v, c.w) : [String(v)]; maxLines = Math.max(maxLines, wrapped[c.key].length); });
        const rowH = 14 * maxLines + 8;
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH + 2, width: RIGHT - (LEFT - 2), height: rowH, color: rgb(0.95,0.95,0.95) });
        for (let ln = 0; ln < maxLines; ln++) { cols.forEach(c => { const txt = wrapped[c.key][ln] || ''; page.drawText(txt, { x: c.x, y: y - 12 - ln*14, size: 10, font: helv }); }); }
        page.drawLine({ start: {x: LEFT, y: y - rowH}, end: {x: RIGHT, y: y - rowH}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (rowH + 6); shaded = !shaded; i++;
      }
    }
  };

  // Q22 continuation (more than 1 storage)
  const q22All = Array.isArray(data.sofa?.q22) ? data.sofa.q22 : [];
  if (q22All.length > 1) {
    const rows = q22All.slice(1).map(r => ({
      inst: r.facility_name || '',
      addr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' '),
      cont: r.contents_description || '',
      access: (Array.isArray(r.others_with_access) ? r.others_with_access.map(a => `${a.name||''}${(a.street||a.city||a.state||a.zip)?' — ':''}${[a.street||'', `${a.city||''}${a.city?', ':''}${a.state||''} ${a.zip||''}`.trim()].filter(Boolean).join(' ')}`).join('; ') : ''),
      still: (r.still_has === true ? 'Yes' : r.still_has === false ? 'No' : '')
    }));
    const COLS22 = [
      { key: 'inst',  label: 'Facility',           x: LEFT,       w: 140, wrap: true },
      { key: 'addr',  label: 'Address',            x: LEFT + 120, w: 170, wrap: true },
      { key: 'cont',  label: 'Contents',           x: LEFT + 300, w: 150, wrap: true },
      { key: 'access',label: 'Others with access', x: LEFT + 450, w: RIGHT - (LEFT + 540), wrap: true },
      { key: 'still', label: 'Still have?',        x: RIGHT - 60, w: 44,  wrap: false },
    ];
    drawTable('Part 5, Question 22: Storage units/facilities (additional entries)', COLS22, rows);
  }

  // Q23 continuation (more than 1 borrowed/held for another)
  const q23All = Array.isArray(data.sofa?.q23) ? data.sofa.q23 : [];
  if (q23All.length > 1) {
    const rows = q23All.slice(1).map(r => ({
      owner: r.owner_name || '',
      ownerAddr: [r.owner_street1||'', r.owner_street2||'', `${r.owner_city||''}${r.owner_city?', ':''}${r.owner_state||''} ${r.owner_zip_code||''}`.trim()].filter(Boolean).join(' '),
      location: [r.property_location_street1||'', r.property_location_street2||'', `${r.property_location_city||''}${r.property_location_city?', ':''}${r.property_location_state||''} ${r.property_location_zip_code||''}`.trim()].filter(Boolean).join(' '),
      desc: r.property_description || '',
      val: money(r.value)
    }));
    const COLS23 = [
      { key: 'owner',     label: 'Owner',        x: LEFT,       w: 100, wrap: true },
      { key: 'ownerAddr', label: 'Owner address',x: LEFT+110,   w: 170, wrap: true },
      { key: 'location',  label: 'Property location', x: LEFT+290, w: 170, wrap: true },
      { key: 'desc',      label: 'Description',  x: LEFT+465,   w: RIGHT-(LEFT+560), wrap: true },
      { key: 'val',       label: 'Value',        x: RIGHT-60,   w: 50,  wrap: false },
    ];
    drawTable('Part 5, Question 23: Property you hold or control for someone else (additional entries)', COLS23, rows);
  }

  // Q24 continuation (more than 1 environmental notice)
  const q24All = Array.isArray(data.sofa?.q24) ? data.sofa.q24 : [];
  if (q24All.length > 1) {
    const rows = q24All.slice(1).map(r => ({
      site: r.site_name || '',
      siteAddr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' '),
      gov: r.government_unit || '',
      govAddr: [`${r.gov_unit_city||''}${r.gov_unit_city?', ':''}${r.gov_unit_state||''} ${r.gov_unit_zip||''}`.trim()].filter(Boolean).join(' '),
      law: r.environmental_law || '',
      date: fmt(r.notice_date)
    }));
    const COLS24 = [
      { key: 'site',    label: 'Site',       x: LEFT,       w: 120, wrap: true },
      { key: 'siteAddr',label: 'Site address', x: LEFT+130,  w: 170, wrap: true },
      { key: 'gov',     label: 'Gov. unit',  x: LEFT+310,   w: 120, wrap: true },
      { key: 'govAddr', label: 'Gov. address', x: LEFT+440, w: RIGHT-(LEFT+520), wrap: true },
      { key: 'law',     label: 'Law/Notice', x: RIGHT-190,  w: 80,  wrap: true },
      { key: 'date',    label: 'Date',       x: RIGHT-60,   w: 50,  wrap: false },
    ];
    drawTable('Part 5, Question 24: Environmental law notice or violation (additional entries)', COLS24, rows);
  }
}
// 3.21) Fill Page 11 (Q25 Hazardous property notices; Q26 Environmental lawsuits; Q27 Business interests)
try {
  const sofaPdfClone11 = await PDFDocument.load(sofaBytes);
  const form11 = sofaPdfClone11.getForm();
  const caseNo11 = data.caption?.case_number || data.case_number || '';
  const debtors11 = data.debtors || data.caption?.debtors || [];
  const d1_11 = debtors11.find(d => d.is_primary && d.is_filing) || debtors11[0] || {};
  const d2_11 = debtors11.find(d => !d.is_primary && d.is_filing) || {};
  const fullName11 = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  try { form11.getTextField('case_number').setText(caseNo11); } catch {}
  try { form11.getTextField('Debtor 1').setText(fullName11(d1_11)); } catch {}
  try { form11.getTextField('Debtor 2').setText(fullName11(d2_11)); } catch {}

  const trySet11  = (n, v) => { try { form11.getTextField(n).setText(v ?? ''); } catch {} };
  const tryCheck11 = (n) => { try { form11.getCheckBox(n).check(); } catch {} };
  const pageSize11 = sofaPdfClone11.getPages()[10]?.getSize?.() || sofaPdfClone11.getPages()[0].getSize();

  const fmt11 = (s) => {
    if (!s) return ''; const [y,m,d] = String(s).split('-');
    return (y && m && d) ? `${m}/${d}/${y}` : '';
  };

  // ===== Q25: Hazardous property notices =====
  const q25 = Array.isArray(data.sofa?.q25) ? data.sofa.q25 : [];
  if (q25.length) tryCheck11('hazerdous_yes'); else tryCheck11('hazerdous_no');

  if (q25[0]) {
    const r = q25[0];
    trySet11('hazerdous_site', r.site_name || '');
    trySet11('hazerdous_env_law', r.environmental_law || '');
    trySet11('hazerdous_date', fmt11(r.notice_date));
    // Government unit + address
    trySet11('hazerdous_gov_unity', r.government_unit || '');
    trySet11('hazerdous_gov_city', r.gov_unit_city || '');
    trySet11('hazerdous_gov_state', r.gov_unit_state || '');
    trySet11('hazerdous_gov_zip', r.gov_unit_zip || '');
    // Site address
    trySet11('hazerdous_street1', r.street1 || '');
    trySet11('hazerdous_street2', r.street2 || '');
    trySet11('hazerdous_city', r.city || '');
    trySet11('hazerdous_state', r.state || '');
    trySet11('hazerdous_zip', r.zip_code || '');
  }

  // ===== Q26: Environmental lawsuits =====
  const q26 = Array.isArray(data.sofa?.q26) ? data.sofa.q26 : [];
  if (q26.length) tryCheck11('epa_lawsuit_yes'); else tryCheck11('epa_lawsuit_no');

  if (q26[0]) {
    const L = q26[0];
    trySet11('epa_lawsuit_case_title', L.case_title || '');
    trySet11('epa_lawsuit_case_number', L.case_number || '');
    trySet11('epa_lawsuit_nature', L.nature_of_case || '');
    trySet11('epa_lawsuit_court', L.court_name || '');

    trySet11('epa_lawsuit_court_street', L.court_street || '');
    trySet11('epa_lawsuit_court_city', L.court_city || '');
    trySet11('epa_lawsuit_court_state', L.court_state || '');
    trySet11('epa_lawsuit_court_zip', L.court_zip || '');

    const s = (L.status || '').toLowerCase();
    if (s.includes('appeal')) tryCheck11('epa_lawsuit_appeal');
    else if (/(conclud|dispos|closed|dismiss)/.test(s)) tryCheck11('epa_lawsuit_concluded');
    else tryCheck11('epa_lawsuit_pending');
  }

  // ===== Q27: Business interests =====
  const q27 = Array.isArray(data.sofa?.q27) ? data.sofa.q27 : [];
  if (q27.length) tryCheck11('business_yes'); else tryCheck11('business_no');

  const markBizType = (rec) => {
    const t = (rec.business_type || '').toLowerCase();
    try {
      if (t === 'sole_proprietor') form11.getCheckBox('sole_proprietor_yes').check();
      if (t === 'llc') form11.getCheckBox('llc_yes').check();
      if (t === 'partnership') form11.getCheckBox('partnership_yes').check();
      if (t === 'officer') form11.getCheckBox('officer_yes').check();
      if (t === 'shareholder' || t === 'corporation') form11.getCheckBox('corporation_yes').check();
    } catch {}
  };

  const fillBiz = (b, slot /*1-based*/) => {
    markBizType(b);
    trySet11(`business_name_${slot}`, b.business_name || '');
    trySet11(`business_street1_${slot}`, b.street1 || '');
    trySet11(`business_street2_${slot}`, b.street2 || '');
    trySet11(`business_city_${slot}`, b.city || '');
    trySet11(`business_state_${slot}`, b.state || '');
    trySet11(`business_zip_${slot}`, b.zip_code || '');
    trySet11(`business_nature_${slot}`, b.nature_of_business || '');
    trySet11(`business_accountant_${slot}`, b.accountant_name || '');
    trySet11(`business_ein_${slot}`, b.ein || '');
    trySet11(`business_start_date_${slot}`, fmt11(b.start_date));
    trySet11(`business_end_date_${slot}`, b.end_date ? fmt11(b.end_date) : 'Present');
  };

  // Ensure business-type checkboxes reflect all records (not just the first two shown on-page)
  (q27 || []).forEach(rec => { try { markBizType(rec); } catch {} });

  if (q27[0]) fillBiz(q27[0], 1);
  if (q27[1]) fillBiz(q27[1], 2);

  moveUncheckedCheckboxesToCorner(form11, pageSize11, sofaPdfClone11.context);
  form11.flatten();
  const tmpBytes11 = await sofaPdfClone11.save();
  const tmpDoc11 = await PDFDocument.load(tmpBytes11);
  const [filledP11] = await out.copyPages(tmpDoc11, [10]); // eleventh page (0-based index 10)
  out.addPage(filledP11);
} catch (e) {
  console.warn('Falling back to blank Page 11 due to fill error:', e);
  try {
    const [blankP11] = await out.copyPages(sofaPdf, [10]);
    out.addPage(blankP11);
  } catch {}
}

// 3.22) Continuation pages for Q25 / Q26 / Q27
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize;
  const LEFT = 16, RIGHT = W - 16;

  const wrap = (text, maxW) => {
    const words = String(text || '').split(/\s+/);
    const lines = []; let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
      else { if (line) lines.push(line); line = w; }
    }
    if (line) lines.push(line);
    return lines;
  };
  const fmt = (s) => { if (!s) return ''; const [y,m,d] = String(s).split('-'); return (y&&m&&d)? `${m}/${d}/${y}`:'' };

  const drawTable = (title, cols, rows) => {
    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText(title, { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      cols.forEach(c => page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv }));
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const wrapped = {}; let maxLines = 1;
        cols.forEach(c => { const v = r[c.key] ?? ''; wrapped[c.key] = c.wrap ? wrap(v, c.w) : [String(v)]; maxLines = Math.max(maxLines, wrapped[c.key].length); });
        const rowH = 14 * maxLines + 8;
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH + 2, width: RIGHT - (LEFT - 2), height: rowH, color: rgb(0.95,0.95,0.95) });
        for (let ln = 0; ln < maxLines; ln++) {
          cols.forEach(c => {
            const txt = wrapped[c.key][ln] || '';
            page.drawText(txt, { x: c.x, y: y - 12 - ln*14, size: 10, font: helv });
          });
        }
        page.drawLine({ start: {x: LEFT, y: y - rowH}, end: {x: RIGHT, y: y - rowH}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (rowH + 6); shaded = !shaded; i++;
      }
    }
  };

  // Q25 continuation (more than 1)
  {
    const q25All = Array.isArray(data.sofa?.q25) ? data.sofa.q25 : [];
    if (q25All.length > 1) {
      const rows = q25All.slice(1).map(r => ({
        site: r.site_name || '',
        gov: r.government_unit || '',
        govAddr: [`${r.gov_unit_city||''}${r.gov_unit_city?', ':''}${r.gov_unit_state||''} ${r.gov_unit_zip||''}`.trim()].filter(Boolean).join(' '),
        law: r.environmental_law || '',
        date: fmt(r.notice_date),
        addr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' ')
      }));
      const COLS25 = [
        { key: 'site',  label: 'Site',        x: LEFT,       w: 140, wrap: true },
        { key: 'gov',   label: 'Gov. unit',   x: LEFT+150,   w: 140, wrap: true },
        { key: 'govAddr', label: 'Gov. address', x: LEFT+300, w: 160, wrap: true },
        { key: 'law',   label: 'Env. law',    x: LEFT+465,   w: 110, wrap: true },
        { key: 'date',  label: 'Date',        x: RIGHT-60,   w: 50,  wrap: false },
      ];
      drawTable('Part 5, Question 25: Hazardous property notices (additional entries)', COLS25, rows);
    }
  }

  // Q26 continuation (more than 1)
  {
    const q26All = Array.isArray(data.sofa?.q26) ? data.sofa.q26 : [];
    if (q26All.length > 1) {
      const rows = q26All.slice(1).map(L => ({
        title: L.case_title || '',
        number: L.case_number || '',
        nature: L.nature_of_case || '',
        court:  L.court_name || '',
        addr:   `Court address: ${[L.court_street||'', `${L.court_city||''}${L.court_city?', ':''}${L.court_state||''} ${L.court_zip||''}`.trim()].filter(Boolean).join(' ')}`,
        status: (()=>{
          const s=(L.status||'').toLowerCase();
          if (s.includes('appeal')) return 'Appealed';
          if (/(conclud|dispos|closed|dismiss)/.test(s)) return 'Concluded';
          return 'Pending';
        })()
      }));
      const COLS26 = [
        { key: 'title',  label: 'Title',      x: LEFT,        w: 170, wrap: true },
        { key: 'number', label: 'Case #',     x: LEFT + 175,  w: 130, wrap: true },
        { key: 'nature', label: 'Nature',     x: LEFT + 310,  w: 120, wrap: true },
        { key: 'court',  label: 'Court',      x: LEFT + 435,  w: 150, wrap: true }, // widened
        { key: 'status', label: 'Status',     x: RIGHT - 60,  w: 50,  wrap: false },
      ];
      drawTable('Part 5, Question 26: Environmental lawsuits (additional entries)', COLS26, rows);
    }
  }

  // Q27 continuation (more than 2)
  {
    const q27All = Array.isArray(data.sofa?.q27) ? data.sofa.q27 : [];
    if (q27All.length > 2) {
      const rows = q27All.slice(2).map(b => ({
        type: (() => {
          const t = (b.business_type || '').toLowerCase();
          if (t === 'sole_proprietor') return 'Sole proprietor';
          if (t === 'llc') return 'LLC';
          if (t === 'partnership') return 'Partnership';
          if (t === 'officer') return 'Officer';
          if (t === 'shareholder') return 'Shareholder';
          if (t === 'corporation') return 'Corporation';
          return '';
        })(),
        name: b.business_name || '',
        addr: [b.street1 || '', b.street2 || '', `${b.city || ''}${b.city ? ', ' : ''}${b.state || ''} ${b.zip_code || ''}`.trim()]
          .filter(Boolean)
          .join(' '),
        nature: b.nature_of_business || '',
        acct: b.accountant_name || '',
        ein: b.ein || '',
        dates: `${fmt(b.start_date) || '—'} — ${b.end_date ? fmt(b.end_date) : 'Present'}`
      }));

      // Base columns (single-row line items)
      const COLS27 = [
        { key: 'type',   label: 'Type',     x: LEFT,       w: 100, wrap: true },
        { key: 'name',   label: 'Business', x: LEFT + 110, w: 160, wrap: true },
        { key: 'addr',   label: 'Address',  x: LEFT + 275, w: 200, wrap: true },
        { key: 'nature', label: 'Nature',   x: LEFT + 480, w: RIGHT - (LEFT + 560), wrap: true }, // ~100–120px
      ];

      // Header for this continuation
      const drawHeader27 = (page) => {
        page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
        page.drawText('Part 6, Question 27: Business interests (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });
        const y0 = H - 96;
        for (const c of COLS27) page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv });
        page.drawLine({ start: { x: LEFT, y: y0 - 4 }, end: { x: RIGHT, y: y0 - 4 }, thickness: 0.7, color: rgb(0.2, 0.2, 0.2) });
      };

      // Wrapper helper for the base columns
      const wrap27 = (text, maxW) => {
        const words = String(text || '').split(/\s+/);
        const lines = [];
        let line = '';
        for (const w of words) {
          const test = line ? `${line} ${w}` : w;
          if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test;
          else {
            if (line) lines.push(line);
            line = w;
          }
        }
        if (line) lines.push(line);
        return lines;
      };

      let i = 0;
      while (i < rows.length) {
        const page = out.addPage(pageSize);
        drawHeader27(page);

        let y = H - 112;
        let shaded = false;

        while (i < rows.length && y > 60) {
          const r = rows[i];

          // Wrap each base column and get the tallest height
          const wrapped = {};
          let maxLines = 1;
          for (const c of COLS27) {
            const val = r[c.key] ?? '';
            wrapped[c.key] = c.wrap ? wrap27(val, c.w) : [String(val)];
            maxLines = Math.max(maxLines, wrapped[c.key].length);
          }
          // Height for base row
          const baseRowH = 14 * maxLines + 8;

          // Compose the details band text (Accountant / EIN / Dates)
          const detailsLabel = 'Details:';
          const detailsLabelW = helv.widthOfTextAtSize(detailsLabel, 10) + 6;
          const detailsValue = [
            r.acct ? `Accountant: ${r.acct}` : 'Accountant: —',
            r.ein ? `EIN: ${r.ein}` : 'EIN: —',
            `Dates: ${r.dates}`
          ].join('   |   ');
          const detailsMaxW = RIGHT - (LEFT + detailsLabelW);
          const detailsLines = wrap27(detailsValue, detailsMaxW);

          // Total block height includes base row + details band (if any)
          const totalH = baseRowH + (detailsLines.length ? (detailsLines.length * 12 + 12) : 0);

          // Shaded background for alternating rows
          if (shaded) {
            page.drawRectangle({
              x: LEFT - 2,
              y: y - totalH + 2,
              width: RIGHT - (LEFT - 2),
              height: totalH,
              color: rgb(0.95, 0.95, 0.95)
            });
          }

          // Draw base row
          for (let ln = 0; ln < maxLines; ln++) {
            for (const c of COLS27) {
              const txt = wrapped[c.key][ln] || '';
              page.drawText(txt, { x: c.x, y: y - 12 - ln * 14, size: 10, font: helv });
            }
          }

          // Draw details band under base row
          let y2 = y - baseRowH;
          if (detailsLines.length) {
            page.drawText(detailsLabel, { x: LEFT, y: y2 - 10, size: 10, font: helv });
            detailsLines.forEach((ln, k) => {
              page.drawText(ln, { x: LEFT + detailsLabelW, y: y2 - 10 - k * 12, size: 10, font: helv });
            });
            y2 -= (detailsLines.length * 12 + 6);
          }

          // Divider line beneath the whole block
          page.drawLine({
            start: { x: LEFT, y: y2 - 6 },
            end: { x: RIGHT, y: y2 - 6 },
            thickness: 0.4,
            color: rgb(0.8, 0.8, 0.8)
          });

          // Advance
          y = y2 - 10;
          shaded = !shaded;
          i++;
        }
      }
    }
  }
}

//Put page 12 here please
// 3.23) Fill Page 12 (Q28 Financial statements; Signatures; Additional pages; Preparer)
try {
  const sofaPdfClone12 = await PDFDocument.load(sofaBytes);
  const form12 = sofaPdfClone12.getForm();

  // Header
  const caseNo12 = data.caption?.case_number || data.case_number || '';
  const debtors12 = data.debtors || data.caption?.debtors || [];
  const d1_12 = debtors12.find(d => d.is_primary && d.is_filing) || debtors12[0] || {};
  const d2_12 = debtors12.find(d => !d.is_primary && d.is_filing) || {};
  const fullName12 = (d) => [d.first_name, d.second_name, d.last_name].filter(Boolean).join(' ');
  try { form12.getTextField('case_number').setText(caseNo12); } catch {}
  try { form12.getTextField('Debtor 1').setText(fullName12(d1_12)); } catch {}
  try { form12.getTextField('Debtor 2').setText(fullName12(d2_12)); } catch {}

  const trySet12  = (n, v) => { try { form12.getTextField(n).setText(v ?? ''); } catch {} };
  const tryCheck12 = (n) => { try { form12.getCheckBox(n).check(); } catch {} };
  const pageSize12 = sofaPdfClone12.getPages()[11]?.getSize?.() || sofaPdfClone12.getPages()[0].getSize();

  const fmt12 = (s) => { if (!s) return ''; const [y,m,d] = String(s).split('-'); return (y&&m&&d)? `${m}/${d}/${y}` : ''; };

  // ===== Q28: Financial statements (page capacity: 1) =====
  const q28 = Array.isArray(data.sofa?.q28) ? data.sofa.q28 : [];
  if (q28.length) tryCheck12('financial_statement_yes'); else tryCheck12('financial_statement_no');

  if (q28[0]) {
    const r = q28[0];
    trySet12('financial_statement_recipient_name',   r.party_name || '');
    trySet12('financial_statement_recipient_date',   fmt12(r.date_issued));
    trySet12('financial_statement_recipient_street1',r.street1 || '');
    trySet12('financial_statement_recipient_street2',r.street2 || '');
    trySet12('financial_statement_recipient_city',   r.city || '');
    trySet12('financial_statement_recipient_state',  r.state || '');
    trySet12('financial_statement_recipient_zip',    r.zip_code || '');
  }

  // ===== Signatures (s/ Full Name) and dates =====
  const execDate  = caseData?.execution_date || data.executed_date || data.caption?.executed_date || '';
  const sigDate   = fmt12(execDate);
  const sig1      = fullName12(d1_12) ? `s/ ${fullName12(d1_12)}` : '';
  const sig2      = fullName12(d2_12) ? `s/ ${fullName12(d2_12)}` : '';
  trySet12('Debtor 1_signature',           sig1);
  if (amendedData.amended) trySet12('Debtor 1_signature_date',     fmtDate(isoToday()));
  else trySet12('Debtor 1_signature_date',     sigDate);
  trySet12('Debtor 2_signature',          sig2);
  if (amendedData.amended) trySet12('Debtor 2_signature_date',     fmtDate(isoToday()));
  else trySet12('Debtor 2_signature_date',     sigDate);
  // Some templates include a confirm checkbox for Debtor 1 signature
  tryCheck12('Debtor 1_signature_yes');

  // ===== Non-attorney preparer (always No for now) =====
  tryCheck12('non_attorney_preparer_no');
  trySet12('non_attorney_preparer_name', '');

  // ===== Additional pages? (Yes if any continuation was needed anywhere) =====
  const hasCont = (() => {
    const len = (x)=>Array.isArray(x)?x.length:0;
    const caps = { // page capacities used in this generator
      q9:2,q10:2,q11:1,q13:2,q14:1,q15:1,q16:2,q17:1,q18:2,q19:1,q20:2,q21:2,q22:1,q23:1,q24:1,q25:1,q26:1,q27:2,q28:1
    };
    const s = data.sofa || {};
    return (
      len(s.q2.primary)>2 || len(s.q6.recent_payments)>3 || len(s.q2.secondary)>2 ||
      len(s.q9)>2 || len(s.q10)>2 || len(s.q11)>1 || len(s.q13)>2 || len(s.q14)>1 ||
      len(s.q15)>1 || len(s.q16)>1 || len(s.q17)>1 || len(s.q18)>2 || len(s.q19)>1 ||
      len(s.q20)>2 || len(s.q21)>2 || len(s.q22)>1 || len(s.q23)>1 || len(s.q24)>1 ||
      len(s.q25)>1 || len(s.q26)>1 || len(s.q27)>2 || len(s.q28)>1
    );
  })();
  if (hasCont) tryCheck12('additional_pages_yes'); else tryCheck12('additional_pages_no');

  // finalize Page 12
  moveUncheckedCheckboxesToCorner(form12, pageSize12, sofaPdfClone12.context);
  form12.flatten();
  const tmpBytes12 = await sofaPdfClone12.save();
  const tmpDoc12 = await PDFDocument.load(tmpBytes12);
  const [filledP12] = await out.copyPages(tmpDoc12, [11]); // twelfth page (0-based)
  out.addPage(filledP12);
} catch (e) {
  console.warn('Falling back to blank Page 12 due to fill error:', e);
  try { const [blankP12] = await out.copyPages(sofaPdf, [11]); out.addPage(blankP12); } catch {}
}

// 3.24) Continuation pages for Q28 (more than 1)
{
  const helv = await out.embedFont(StandardFonts.Helvetica);
  const pageSize = [612, 792];
  const [W, H] = pageSize; const LEFT = 16, RIGHT = W - 16;

  const wrap = (text, maxW) => {
    const words = String(text || '').split(/\s+/); const lines = []; let line = '';
    for (const w of words) { const test = line ? `${line} ${w}` : w; if (helv.widthOfTextAtSize(test, 10) <= maxW) line = test; else { if (line) lines.push(line); line = w; } }
    if (line) lines.push(line); return lines;
  };
  const fmt = (s) => { if (!s) return ''; const [y,m,d] = String(s).split('-'); return (y&&m&&d)? `${m}/${d}/${y}` : ''; };

  const q28All = Array.isArray(data.sofa?.q28) ? data.sofa.q28 : [];
  if (q28All.length > 1) {
    const rows = q28All.slice(1).map(r => ({
      name: r.party_name || '',
      addr: [r.street1||'', r.street2||'', `${r.city||''}${r.city?', ':''}${r.state||''} ${r.zip_code||''}`.trim()].filter(Boolean).join(' '),
      date: fmt(r.date_issued)
    }));

    const COLS = [
      { key: 'name', label: 'Recipient', x: LEFT,       w: 180, wrap: true },
      { key: 'addr', label: 'Address',   x: LEFT + 190, w: 260, wrap: true },
      { key: 'date', label: 'Date',      x: RIGHT - 80, w: 70,  wrap: false },
    ];

    let i = 0;
    while (i < rows.length) {
      const page = out.addPage(pageSize);
      page.drawText('Continuation Sheet — Official Form 107 Statement of Financial Affairs', { x: LEFT, y: H - 50, size: 14, font: helv });
      page.drawText('Part 7, Question 28: Financial statements (additional entries)', { x: LEFT, y: H - 68, size: 12, font: helv });
      const y0 = H - 96;
      COLS.forEach(c => page.drawText(c.label, { x: c.x, y: y0, size: 10, font: helv }));
      page.drawLine({ start: {x: LEFT, y: y0 - 4}, end: {x: RIGHT, y: y0 - 4}, thickness: 0.7, color: rgb(0.2,0.2,0.2) });

      let y = H - 112; let shaded = false;
      while (i < rows.length && y > 60) {
        const r = rows[i];
        const wrapped = {}; let maxLines = 1;
        COLS.forEach(c => { const v = r[c.key] ?? ''; wrapped[c.key] = c.wrap ? wrap(v, c.w) : [String(v)]; maxLines = Math.max(maxLines, wrapped[c.key].length); });
        const rowH = 14 * maxLines + 8;
        if (shaded) page.drawRectangle({ x: LEFT - 2, y: y - rowH + 2, width: RIGHT - (LEFT - 2), height: rowH, color: rgb(0.95,0.95,0.95) });
        for (let ln = 0; ln < maxLines; ln++) { COLS.forEach(c => { const txt = wrapped[c.key][ln] || ''; page.drawText(txt, { x: c.x, y: y - 12 - ln*14, size: 10, font: helv }); }); }
        page.drawLine({ start: {x: LEFT, y: y - rowH}, end: {x: RIGHT, y: y - rowH}, thickness: 0.4, color: rgb(0.8,0.8,0.8) });
        y -= (rowH + 6); shaded = !shaded; i++;
      }
    }
  }
}

// Finally if the SOFA is being amended we need a notice of amendment, certificate of service, and then generate the envelopes from the amendedData.
  // Helper for long date used on amendment form
  const fmtLongDate = (s) => {
    if (!s) return '';
    try {
      // If already a Date, format using local time
      if (s instanceof Date) {
        return s.toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
        });
      }
      const str = String(s).trim();
      // Parse ISO YYYY-MM-DD as a LOCAL date to prevent timezone rollback
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]) - 1; // 0-based month
        const d = Number(m[3]);
        const local = new Date(y, mo, d); // local midnight
        return local.toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
        });
      }
      // Fallback: let Date try to parse other formats, formatted in local time
      const dt = new Date(str);
      if (!isNaN(dt)) {
        return dt.toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
        });
      }
      return '';
    } catch {
      return '';
    }
  };

  let finalPdfDoc = out; // by default, just the generated SOFA

  if (amendedData?.amended) {
    try {
      // Load Notice of Amendment shell
      const amendedUrl = '/forms/notice-of-amendment.pdf';
      const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
      const amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
      const amendedForm = amendedPdfDoc.getForm();

      // Fill amendment cover
      const debtorsAO = filingDebtors(amendedData.caseInfo?.debtors || data.debtors);
      debtorsAO.forEach((debtor) => {
        const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
        try {
          amendedForm.getTextField(`${amendPrefix}`).setText(
            `${debtor.first_name || ''} ${debtor.second_name || ''} ${debtor.last_name || ''} ${debtor.suffix || ''}`.trim()
          );
        } catch {}
      });
      try { amendedForm.getTextField('case_number').setText(amendedData.caseInfo?.case_number || data.caption?.case_number || data.case_number || ''); } catch {}
      try { amendedForm.getTextField('chapter').setText(String(amendedData.caseInfo?.chapter || data.chapter || '')); } catch {}
      try { amendedForm.getTextField('date').setText(fmtLongDate(isoToday())); } catch {}

      // What is being amended: Statement of Financial Affairs
      try { amendedForm.getTextField('amended_files').setText('Statement of Financial Affairs'); } catch {}
      try { amendedForm.getTextField('items_served').setText('Amended Statement of Financial Affairs, and Notice of Amendment'); } catch {}
      try { amendedForm.getTextField('reason_amended').setText(amendedData.amendedReason || ''); } catch {}
      try {
        amendedForm.getTextField('creditors_served_description').setText(
          amendmentServiceDescription({ ...(data || {}), ...(amendedData || {}) }),
        );
      } catch {}

      // Update appearances and flatten cover
      try {
        const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
        amendedForm.updateFieldAppearances(helvetica);
      } catch {}
      try { amendedForm.flatten(); } catch {}

      // Merge: main SOFA + amendment cover (+ optional creditor list) into a single doc
      const mergedPdf = await PDFDocument.create();
      const mainPages = await mergedPdf.copyPages(out, out.getPageIndices());
      mainPages.forEach(p => mergedPdf.addPage(p));

      const amendedPages = await mergedPdf.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
      amendedPages.forEach(p => mergedPdf.addPage(p));

      // Append either the court mailing matrix or the manually selected list.
      try {
        await appendAmendmentServiceList(
          mergedPdf,
          { ...(data || {}), ...(amendedData || {}) },
          generateCreditorsListPdf,
        );
      } catch (e) { console.warn('Could not append creditors list for amendment:', e); }

      finalPdfDoc = mergedPdf;
    } catch (amErr) {
      console.warn('Amendment handling failed; proceeding with base SOFA only:', amErr);
      finalPdfDoc = out; // fallback
    }
  }
// 4) Save and download (use merged doc if amended)
    const bytes = await finalPdfDoc.save();
    const blob = new Blob([bytes], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('file', blob, 'draft.pdf');

        try {
          const res = await fetch(`${API_URL2}/optimize-pdf/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
          });
          if (res.ok) {
            const optBlob = await res.blob();
            return optBlob || blob;
          }
          // fall back to original if optimization service fails
          return blob;
        } catch (err) {
          console.error('Optimization failed, returning original blob:', err);
          return blob;
        }
    
  } catch (e) {
    console.error('Failed to generate SOFA:', e);
    alert('Failed to generate SOFA: ' + (e?.message || e));
  }

  
}
